import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getBroadcastCampaigns, createBroadcastCampaign, getMetaTemplates, getBroadcastPreview, getBroadcastPreviewList, runBroadcastCampaign, cancelBroadcastCampaign, updateBroadcastCampaign, deleteBroadcastCampaign, getWhatsAppIntegrations, getBroadcastCampaignRecipients, getBroadcastDeliveryEvents, getBroadcastCampaignAuditLog, retryBroadcastRecipients, sendWhatsAppMessage, testSendWhatsAppTemplate, getWhatsAppSnippets, createWhatsAppSnippet, updateWhatsAppSnippet, deleteWhatsAppSnippet, uploadWhatsAppMedia } from '../utils/api';
import { FaBullhorn, FaChevronDown, FaChevronUp, FaFilter, FaCalendarAlt, FaTimes, FaEdit, FaInfoCircle, FaFileAlt, FaSyncAlt, FaTrash, FaComments, FaBold, FaItalic, FaStrikethrough, FaCode, FaLink, FaListUl, FaListOl, FaImage, FaMapMarkerAlt, FaMusic, FaVideo, FaSmile, FaPaperclip, FaUpload, FaCheck, FaMicrophone, FaCopy, FaHistory } from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useToast } from './Toast';
import WhatsAppTemplates from './WhatsAppTemplates';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview';
import './WhatsApp.css';

// Fix Leaflet marker icon
if (typeof L !== 'undefined' && L.Icon && L.Icon.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

// Location marker for map click - must be inside MapContainer
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
};

// Area = Region/City (المنطقة/المدينة) - broader geographic level
const AREA_OPTIONS = [
  { value: '', labelAr: 'الكل', labelEn: 'All' },
  { value: 'الرياض', labelAr: 'الرياض', labelEn: 'Riyadh' },
  { value: 'جدة', labelAr: 'جدة', labelEn: 'Jeddah' },
  { value: 'مكة', labelAr: 'مكة', labelEn: 'Makkah' },
  { value: 'المدينة', labelAr: 'المدينة المنورة', labelEn: 'Madinah' },
  { value: 'الدمام', labelAr: 'الدمام', labelEn: 'Dammam' },
  { value: 'الخبر', labelAr: 'الخبر', labelEn: 'Khobar' },
  { value: 'الطائف', labelAr: 'الطائف', labelEn: 'Taif' },
  { value: 'بريدة', labelAr: 'بريدة', labelEn: 'Buraidah' },
  { value: 'تبوك', labelAr: 'تبوك', labelEn: 'Tabuk' },
  { value: 'أبها', labelAr: 'أبها', labelEn: 'Abha' },
  { value: 'القطيف', labelAr: 'القطيف', labelEn: 'Qatif' }
];

// Group areas by region for organized dropdown (used in edit modal)
const AREA_GROUPS = [
  { groupAr: 'المنطقة الوسطى', groupEn: 'Central', options: [{ value: '', labelAr: 'الكل', labelEn: 'All' }, ...AREA_OPTIONS.filter(a => ['الرياض', 'بريدة'].includes(a.value))] },
  { groupAr: 'المنطقة الغربية', groupEn: 'Western', options: AREA_OPTIONS.filter(a => ['جدة', 'مكة', 'المدينة', 'الطائف'].includes(a.value)) },
  { groupAr: 'المنطقة الشرقية', groupEn: 'Eastern', options: AREA_OPTIONS.filter(a => ['الدمام', 'الخبر', 'القطيف'].includes(a.value)) },
  { groupAr: 'أخرى', groupEn: 'Other', options: AREA_OPTIONS.filter(a => ['تبوك', 'أبها'].includes(a.value)) }
];

// Neighborhood = District (الحي) - grouped by city for cascading
const NEIGHBORHOOD_BY_AREA = {
  'الرياض': [
    { value: 'العليا', labelAr: 'العليا', labelEn: 'Al Olaya' },
    { value: 'الملز', labelAr: 'الملز', labelEn: 'Al Malaz' },
    { value: 'النخيل', labelAr: 'النخيل', labelEn: 'Al Nakheel' },
    { value: 'الشميسي', labelAr: 'الشميسي', labelEn: 'Al Shumaisi' },
    { value: 'البديعة', labelAr: 'البديعة', labelEn: 'Al Badi\'ah' },
    { value: 'الروضة', labelAr: 'الروضة', labelEn: 'Al Rawdah' },
    { value: 'الربوة', labelAr: 'الربوة', labelEn: 'Al Rabwah' },
    { value: 'الدرعية', labelAr: 'الدرعية', labelEn: 'Diriyah' },
    { value: 'النسيم', labelAr: 'النسيم', labelEn: 'Al Nasim' },
    { value: 'اليرموك', labelAr: 'اليرموك', labelEn: 'Al Yarmouk' }
  ],
  'جدة': [
    { value: 'الحمراء', labelAr: 'الحمراء', labelEn: 'Al Hamra' },
    { value: 'الروضة', labelAr: 'الروضة', labelEn: 'Al Rawdah' },
    { value: 'الزهراء', labelAr: 'الزهراء', labelEn: 'Al Zahra' },
    { value: 'الشرفية', labelAr: 'الشرفية', labelEn: 'Al Sharafiya' },
    { value: 'النسيم', labelAr: 'النسيم', labelEn: 'Al Nasim' }
  ],
  'الدمام': [
    { value: 'الفناتير', labelAr: 'الفناتير', labelEn: 'Al Fannateer' },
    { value: 'الفيصلية', labelAr: 'الفيصلية', labelEn: 'Al Faisaliyah' },
    { value: 'الروضة', labelAr: 'الروضة', labelEn: 'Al Rawdah' }
  ]
};

const normalizeRecipientPhone = (value = '') => {
  const digits = String(value || '').replace(/[^\d]/g, '');
  if (digits.length < 8 || digits.length > 16) return null;
  return digits;
};

const extractRecipientPhones = (rawText = '') => {
  const tokens = String(rawText || '').split(/[\n,،;\t ]+/).map((t) => t.trim()).filter(Boolean);
  const unique = [];
  const seen = new Set();
  tokens.forEach((token) => {
    const normalized = normalizeRecipientPhone(token);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    unique.push(normalized);
  });
  return unique;
};

const WhatsAppCampaigns = ({
  language = 'ar',
  userRole = 'employee',
  initialSubTab = 'campaigns',
  onTabChange,
  onOpenCampaignReport,
  allowCampaignsSubTab = true,
  allowTemplatesSubTab = true,
}) => {
  const toast = useToast();
  const STATUS_FILTER_STORAGE_KEY = 'wa_campaigns_status_filter_v1';
  const isManagerOrOwner = userRole === 'manager' || userRole === 'owner';
  const [loading, setLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [audiencePreviewList, setAudiencePreviewList] = useState([]);
  const [audiencePreviewMeta, setAudiencePreviewMeta] = useState({ mode: 'auto', total: 0, shown: 0 });
  const [runningCampaignId, setRunningCampaignId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editingScheduleValue, setEditingScheduleValue] = useState('');
  const [detailCampaignId, setDetailCampaignId] = useState(null);
  const [hoveredCampaignId, setHoveredCampaignId] = useState(null);
  const [expandedManagerCampaignId, setExpandedManagerCampaignId] = useState(null);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editingCampaignForm, setEditingCampaignForm] = useState(null);
  const [recipientReportCampaignId, setRecipientReportCampaignId] = useState(null);
  const [recipientReportLoading, setRecipientReportLoading] = useState(false);
  const [recipientReportError, setRecipientReportError] = useState('');
  const [recipientReportRows, setRecipientReportRows] = useState([]);
  const [recipientReportSummary, setRecipientReportSummary] = useState(null);
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('all');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [campaignTraceTab, setCampaignTraceTab] = useState('recipients'); // recipients | delivery | activity
  const [deliveryEvents, setDeliveryEvents] = useState([]);
  const [deliveryEventsLoading, setDeliveryEventsLoading] = useState(false);
  const [deliveryEventsError, setDeliveryEventsError] = useState('');
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditEntriesLoading, setAuditEntriesLoading] = useState(false);
  const [auditEntriesError, setAuditEntriesError] = useState('');
  const [timelineDrawer, setTimelineDrawer] = useState(null); // { campaignId, recipientId, phone, displayName } | null
  const [timelineState, setTimelineState] = useState({ events: [], loading: false, error: '' });
  const [targetedRecipients, setTargetedRecipients] = useState([]);
  const [retryingRecipients, setRetryingRecipients] = useState(new Set());
  const [retryConfirmation, setRetryConfirmation] = useState(null);
  const [manualRecipientsInput, setManualRecipientsInput] = useState('');
  const [uploadedRecipientFileName, setUploadedRecipientFileName] = useState('');
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [subTab, setSubTab] = useState(() => {
    if (!allowCampaignsSubTab && allowTemplatesSubTab) return 'templates';
    if (allowCampaignsSubTab && !allowTemplatesSubTab) return 'campaigns';
    return initialSubTab === 'templates' ? 'templates' : 'campaigns';
  }); // 'campaigns' | 'templates'
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [snippetsSaving, setSnippetsSaving] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [editingSnippetId, setEditingSnippetId] = useState(null);
  const [snippetForm, setSnippetForm] = useState({
    category: 'general',
    label: '',
    content: '',
    message_type: 'text',
    media_metadata: {}
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalUrl, setLinkModalUrl] = useState('');
  const [linkModalText, setLinkModalText] = useState('');
  const [mediaUploading, setMediaUploading] = useState({ image: false, video: false, audio: false });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [smartUrlModal, setSmartUrlModal] = useState(null); // { url, type: 'image'|'video'|'audio' }
  const [expandedSnippets, setExpandedSnippets] = useState(new Set()); // Track which snippets are expanded
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [testSendPhone, setTestSendPhone] = useState('');
  const [testSendStatus, setTestSendStatus] = useState(null); // null | 'sending' | 'ok' | 'error'
  const [testSendMessage, setTestSendMessage] = useState('');
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    template_name: '',
    scheduled_for: '',
    target_filter: {
      client_readiness: null,
      property_type: null,
      area: null,
      neighborhood: null,
      tags: [],
      manual_numbers: [],
      source_phone_number_id: null,
      recipient_mode: 'auto'
    }
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    if (!allowCampaignsSubTab && allowTemplatesSubTab) {
      setSubTab('templates');
      return;
    }
    if (allowCampaignsSubTab && !allowTemplatesSubTab) {
      setSubTab('campaigns');
      return;
    }
    if (initialSubTab === 'templates') setSubTab('templates');
    else setSubTab('campaigns');
  }, [initialSubTab, allowCampaignsSubTab, allowTemplatesSubTab]);

  const loadSnippets = async () => {
    try {
      setSnippetsLoading(true);
      const response = await getWhatsAppSnippets();
      setSnippets(Array.isArray(response.data?.snippets) ? response.data.snippets : []);
    } catch (error) {
      console.error('Failed to load snippets:', error);
      toast.error(language === 'ar' ? 'تعذر تحميل الردود الجاهزة' : 'Failed to load quick replies');
    } finally {
      setSnippetsLoading(false);
    }
  };


  useEffect(() => {
    getMetaTemplates().then(r => setMetaTemplates(r.data?.templates || [])).catch(() => setMetaTemplates([]));
  }, []);

  useEffect(() => {
    getWhatsAppIntegrations()
      .then((r) => setAvailableIntegrations(Array.isArray(r.data?.integrations) ? r.data.integrations : []))
      .catch(() => setAvailableIntegrations([]));
  }, []);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
      if (!saved) return;
      const allowed = ['all', 'running', 'completed', 'scheduled', 'draft', 'cancelled'];
      if (allowed.includes(saved)) setStatusFilter(saved);
    } catch (_) {
      // Ignore storage read errors and keep default filter.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STATUS_FILTER_STORAGE_KEY, statusFilter);
    } catch (_) {
      // Ignore storage write errors; filtering still works in memory.
    }
  }, [statusFilter]);

  const previewDebounceRef = useRef(null);
  const modalBodyRef = useRef(null);
  const recipientSectionRef = useRef(null);
  const snippetContentRef = useRef(null);

  // Formatting rendering function (bold, italic, strikethrough, monospace, URLs)
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    const lines = String(rawText).split('\n');
    const output = [];
    const urlRe = /(https?:\/\/[^\s]+)/g;
    const formatRe = /\*([^*]+)\*|_([^_]+)_|~([^~]+)~|`([^`]+)`/g;
    for (let li = 0; li < lines.length; li++) {
      if (li > 0) output.push(<br key={`br${li}`} />);
      const line = lines[li];
      const parts = line.split(urlRe);
      const lineOutput = [];
      parts.forEach((part, pi) => {
        if (/^https?:\/\//.test(part)) {
          lineOutput.push(<a key={`${li}u${pi}`} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{part}</a>);
          return;
        }
        let cursor = 0;
        let m;
        formatRe.lastIndex = 0;
        while ((m = formatRe.exec(part)) !== null) {
          if (m.index > cursor) {
            lineOutput.push(<span key={`${li}p${pi}-${cursor}`}>{part.slice(cursor, m.index)}</span>);
          }
          if (m[1] !== undefined) {
            lineOutput.push(<strong key={`${li}b${pi}-${m.index}`} style={{ fontWeight: 700 }}>{m[1]}</strong>);
          } else if (m[2] !== undefined) {
            lineOutput.push(<em key={`${li}i${pi}-${m.index}`}>{m[2]}</em>);
          } else if (m[3] !== undefined) {
            lineOutput.push(<s key={`${li}s${pi}-${m.index}`}>{m[3]}</s>);
          } else if (m[4] !== undefined) {
            lineOutput.push(<code key={`${li}c${pi}-${m.index}`} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px', fontSize: '0.9em' }}>{m[4]}</code>);
          }
          cursor = m.index + m[0].length;
        }
        if (cursor < part.length) {
          lineOutput.push(<span key={`${li}p${pi}-${cursor}`}>{part.slice(cursor)}</span>);
        }
      });
      output.push(<span key={`line${li}`}>{lineOutput}</span>);
    }
    return output;
  };

  // Insert formatting at cursor position for Quick Replies
  const insertSnippetFormatting = (formatType, extraData) => {
    const textarea = snippetContentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = snippetForm.content;
    const selectedText = text.substring(start, end);

    let before, after, replacement;
    switch (formatType) {
      case 'bold':
        before = '*';
        after = '*';
        break;
      case 'italic':
        before = '_';
        after = '_';
        break;
      case 'strikethrough':
        before = '~';
        after = '~';
        break;
      case 'monospace':
        before = '`';
        after = '`';
        break;
      case 'link':
        if (extraData?.url) {
          const displayText = extraData.text || extraData.url;
          replacement = `${displayText} ${extraData.url}`;
          break;
        }
        return;
      case 'bullet':
        replacement = selectedText ? `• ${selectedText}` : '• ';
        break;
      case 'numbered':
        replacement = selectedText ? `1. ${selectedText}` : '1. ';
        break;
      default:
        return;
    }

    if (formatType === 'bullet' || formatType === 'numbered') {
      // For lists, replacement is already set
    } else if (formatType === 'link') {
      // replacement already set
    } else if (selectedText) {
      replacement = `${before}${selectedText}${after}`;
    } else {
      replacement = replacement || `${before}${after}`;
    }

    const newContent = text.substring(0, start) + replacement + text.substring(end);
    setSnippetForm({ ...snippetForm, content: newContent });

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + replacement.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertEmoji = (emojiData) => {
    const textarea = snippetContentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const text = snippetForm.content;
    const emoji = emojiData.emoji || emojiData;
    const newContent = text.substring(0, start) + emoji + text.substring(start);
    setSnippetForm({ ...snippetForm, content: newContent });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
    setShowEmojiPicker(false);
  };

  const openLinkModal = () => {
    const textarea = snippetContentRef.current;
    const selectedText = textarea ? snippetForm.content.substring(textarea.selectionStart, textarea.selectionEnd) : '';
    setLinkModalText(selectedText);
    setLinkModalUrl('');
    setShowLinkModal(true);
  };

  const applyLink = () => {
    if (!linkModalUrl.trim()) return;
    insertSnippetFormatting('link', { url: linkModalUrl.trim(), text: linkModalText.trim() || linkModalUrl.trim() });
    setShowLinkModal(false);
    setLinkModalUrl('');
    setLinkModalText('');
  };

  // Smart URL detection: check if URL looks like image/video/audio
  const detectMediaUrlType = (url) => {
    if (!url || typeof url !== 'string') return null;
    const u = url.trim().toLowerCase();
    const imgExt = /\.(jpe?g|png|gif|webp)(\?|$)/i;
    const vidExt = /\.(mp4|webm|mov|avi)(\?|$)/i;
    const audExt = /\.(ogg|mp3|m4a|wav|aac)(\?|$)/i;
    if (imgExt.test(u)) return 'image';
    if (vidExt.test(u)) return 'video';
    if (audExt.test(u)) return 'audio';
    return null;
  };

  const handleContentPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
    if (!pasted) return;
    const type = detectMediaUrlType(pasted);
    if (type) {
      e.preventDefault();
      setSmartUrlModal({ url: pasted.trim(), type });
    }
  };

  const applySmartUrl = () => {
    if (!smartUrlModal) return;
    const { url, type } = smartUrlModal;
    setSnippetForm({
      ...snippetForm,
      media_metadata: {
        ...snippetForm.media_metadata,
        [type]: { url }
      }
    });
    setSmartUrlModal(null);
    toast.success(language === 'ar' ? `تم إرفاق ${type === 'image' ? 'صورة' : type === 'video' ? 'فيديو' : 'صوت'}` : `Attached ${type}`);
  };

  const handleMediaUpload = async (file, type) => {
    const prefix = type === 'image' ? 'image' : type === 'video' ? 'video' : 'audio';
    if (!file.type.startsWith(prefix + '/')) {
      toast.error(language === 'ar' ? `نوع الملف غير مدعوم. استخدم ${type === 'image' ? 'صورة' : type === 'video' ? 'فيديو' : 'صوت'}` : `Use ${type} file`);
      return;
    }
    setMediaUploading(prev => ({ ...prev, [type]: true }));
    try {
      const res = await uploadWhatsAppMedia(file);
      const url = res.data?.url;
      if (url) {
        setSnippetForm({
          ...snippetForm,
          media_metadata: {
            ...snippetForm.media_metadata,
            [type]: { url }
          }
        });
        toast.success(language === 'ar' ? 'تم الرفع بنجاح' : 'Uploaded successfully');
      } else throw new Error('No URL returned');
    } catch (err) {
      toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setMediaUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleMediaDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleMediaUpload(file, type);
  };

  const handleMediaDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Detect text direction for preview
  const detectTextDirection = (text) => {
    if (!text) return language === 'ar' ? 'rtl' : 'ltr';
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text) ? 'rtl' : 'ltr';
  };

  useEffect(() => {
    if (!showCreateForm) return;
    // Debounce: wait 400 ms after the last filter change before firing API calls
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(() => {
    const tags = campaignForm.target_filter?.tags;
    const tagsArr = Array.isArray(tags) ? tags : (String(tags || '')).split(',').map(s => s.trim()).filter(Boolean);
    const filter = { ...campaignForm.target_filter, tags: tagsArr };
    getBroadcastPreview(filter)
      .then(r => {
        setAudiencePreview(r.data?.count ?? null);
        setAudiencePreviewMeta((prev) => ({ ...prev, mode: r.data?.mode || prev.mode }));
      })
      .catch(() => setAudiencePreview(null));
    getBroadcastPreviewList(filter, 250)
      .then((r) => {
        const payload = r?.data || {};
        const list = Array.isArray(payload.recipients) ? payload.recipients : [];
        setAudiencePreviewList(list);
        setAudiencePreviewMeta({
          mode: payload.mode || 'auto',
          total: Number(payload.total || 0),
          shown: Number(payload.shown || list.length || 0)
        });
      })
      .catch(() => {
        setAudiencePreviewList([]);
        setAudiencePreviewMeta({ mode: 'auto', total: 0, shown: 0 });
      });
    }, 400); // 400 ms debounce
    return () => clearTimeout(previewDebounceRef.current);
  }, [showCreateForm, campaignForm.target_filter]);

  const loadCampaigns = async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);
      setCampaignsError(null);
      const response = await getBroadcastCampaigns();
      setCampaigns(response.data?.campaigns || []);
      setLastUpdatedAt(new Date());
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaignsError(language === 'ar' ? 'تعذّر تحميل الحملات. تحقق من الاتصال.' : 'Failed to load campaigns. Check your connection.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const getRefreshMode = () => {
    const now = Date.now();
    const hasRunning = campaigns.some((c) => ['sending', 'running'].includes(String(c.status || '').toLowerCase())) || !!runningCampaignId;
    if (hasRunning) return 'live';
    const hasRecentlyCompleted = campaigns.some((c) => {
      if (String(c.status || '').toLowerCase() !== 'completed') return false;
      const ts = c.completed_at || c.started_at || c.updated_at || c.created_at;
      const ms = ts ? new Date(ts).getTime() : NaN;
      // Poll for 2 minutes after completion to pick up final sent/failed counts
      return Number.isFinite(ms) && (now - ms <= 2 * 60 * 1000);
    });
    return hasRecentlyCompleted ? 'recent' : 'off';
  };

  useEffect(() => {
    if (subTab !== 'campaigns') return undefined;
    const mode = getRefreshMode();
    // Use faster polling right after completion so stats appear quickly
    const intervalMs = mode === 'live' ? 3000 : mode === 'recent' ? 5000 : 0;
    if (!intervalMs) return undefined;
    const timer = setInterval(() => {
      loadCampaigns({ showLoading: false });
    }, intervalMs);
    return () => clearInterval(timer);
  }, [campaigns, runningCampaignId, subTab]);

  const META_ERROR_MAP = {
    131049: {
      ar: 'حد الرسائل التسويقية — لم تُسلّم الرسالة للحفاظ على جودة التواصل',
      en: 'Marketing frequency limit — message not delivered',
      category: 'frequency',
      retryable: true,
      adviceAr: 'أعد الإرسال بعد ٢٤ ساعة. فكّر في تقليل تكرار حملاتك التسويقية أو تخصيص الرسالة لزيادة التفاعل.',
      adviceEn: 'Retry after 24h. Consider reducing campaign frequency or personalizing messages to boost engagement.',
    },
    131026: {
      ar: 'الرسالة غير قابلة للتسليم حالياً',
      en: 'Message currently undeliverable',
      category: 'undeliverable',
      retryable: true,
      adviceAr: 'قد يكون الرقم مؤقتاً غير متاح. أعد المحاولة لاحقاً أو تواصل مع العميل عبر قناة أخرى.',
      adviceEn: 'The number may be temporarily unavailable. Retry later or contact the client through another channel.',
    },
    130472: {
      ar: 'الرقم ضمن تجربة داخلية من ميتا',
      en: 'Number is part of a Meta internal experiment',
      category: 'meta_experiment',
      retryable: false,
      adviceAr: 'هذا قرار من ميتا ولا يمكن التحكم فيه. لا داعي للمحاولة — تواصل مع العميل مباشرة.',
      adviceEn: 'This is a Meta-side decision, outside your control. No retry needed — contact the client directly.',
    },
    131053: {
      ar: 'الرقم غير مسجل في واتساب',
      en: 'Number not registered on WhatsApp',
      category: 'invalid_number',
      retryable: false,
      adviceAr: 'تحقق من الرقم مع العميل أو احذفه من قائمتك.',
      adviceEn: 'Verify the number with the client or remove it from your list.',
    },
    131047: {
      ar: 'انتهت نافذة المحادثة (٢٤ ساعة)',
      en: '24h conversation window expired',
      category: 'window_expired',
      retryable: false,
      adviceAr: 'يجب استخدام قالب تسويقي معتمد للتواصل خارج نافذة ٢٤ ساعة.',
      adviceEn: 'Use an approved marketing template to re-engage outside the 24h window.',
    },
    130429: {
      ar: 'تم تجاوز الحد الأقصى للإرسال',
      en: 'Sending rate limit reached',
      category: 'rate_limit',
      retryable: true,
      adviceAr: 'انتظر بضع دقائق وأعد الإرسال.',
      adviceEn: 'Wait a few minutes and retry.',
    },
    132001: {
      ar: 'القالب غير موجود أو غير معتمد من ميتا',
      en: 'Template not found or not approved by Meta',
      category: 'template_error',
      retryable: false,
      adviceAr: 'تحقق من حالة القالب في صفحة القوالب وأعد تقديمه إذا لزم.',
      adviceEn: 'Check the template status in the Templates page and resubmit if needed.',
    },
    131031: {
      ar: 'حساب الأعمال مقيّد مؤقتاً من قبل ميتا',
      en: 'Business account temporarily restricted by Meta',
      category: 'account_restricted',
      retryable: false,
      adviceAr: 'تواصل مع دعم ميتا لرفع القيود عن حسابك.',
      adviceEn: 'Contact Meta support to lift restrictions on your account.',
    },
    131056: {
      ar: 'تم حظرك من قبل المستلم',
      en: 'Blocked by recipient',
      category: 'blocked',
      retryable: false,
      adviceAr: 'لا يمكن إرسال رسائل لهذا الرقم — احذفه من قائمتك.',
      adviceEn: 'Cannot send to this number — remove it from your list.',
    },
  };

  const FAILURE_CATEGORY_META = {
    frequency:         { icon: '⏱️', colorBg: '#fff7ed', colorBorder: '#fdba74', colorText: '#9a3412', labelAr: 'حد التسويق', labelEn: 'Freq. limit' },
    undeliverable:     { icon: '📵', colorBg: '#fefce8', colorBorder: '#fde047', colorText: '#854d0e', labelAr: 'غير قابل للتسليم', labelEn: 'Undeliverable' },
    meta_experiment:   { icon: '🔬', colorBg: '#f5f3ff', colorBorder: '#c4b5fd', colorText: '#5b21b6', labelAr: 'تجربة ميتا', labelEn: 'Meta experiment' },
    invalid_number:    { icon: '🚫', colorBg: '#fef2f2', colorBorder: '#fecaca', colorText: '#991b1b', labelAr: 'رقم غير صحيح', labelEn: 'Invalid number' },
    window_expired:    { icon: '🕐', colorBg: '#f0f9ff', colorBorder: '#bae6fd', colorText: '#075985', labelAr: 'انتهت النافذة', labelEn: 'Window expired' },
    rate_limit:        { icon: '🔁', colorBg: '#fff7ed', colorBorder: '#fdba74', colorText: '#9a3412', labelAr: 'حد الإرسال', labelEn: 'Rate limit' },
    template_error:    { icon: '📋', colorBg: '#fef2f2', colorBorder: '#fecaca', colorText: '#991b1b', labelAr: 'خطأ في القالب', labelEn: 'Template error' },
    account_restricted:{ icon: '⛔', colorBg: '#fef2f2', colorBorder: '#fecaca', colorText: '#991b1b', labelAr: 'حساب مقيّد', labelEn: 'Account restricted' },
    blocked:           { icon: '🚷', colorBg: '#fef2f2', colorBorder: '#fecaca', colorText: '#991b1b', labelAr: 'محظور', labelEn: 'Blocked' },
    unknown:           { icon: '❓', colorBg: '#f8fafc', colorBorder: '#e2e8f0', colorText: '#475569', labelAr: 'خطأ غير محدد', labelEn: 'Unknown error' },
  };
  const translateMetaError = (errorMessage) => {
    if (!errorMessage) return null;
    const codeMatch = String(errorMessage).match(/\[(\d+)\]/);
    const code = codeMatch ? parseInt(codeMatch[1], 10) : null;
    if (code && META_ERROR_MAP[code]) return META_ERROR_MAP[code];
    const textMatch = String(errorMessage).match(/(\d{6})/);
    const textCode = textMatch ? parseInt(textMatch[1], 10) : null;
    if (textCode && META_ERROR_MAP[textCode]) return META_ERROR_MAP[textCode];
    return null;
  };
  const isRetryableError = (errorMessage) => {
    const mapped = translateMetaError(errorMessage);
    if (mapped) return mapped.retryable;
    const msg = String(errorMessage || '').toLowerCase();
    return msg.includes('131049') || msg.includes('130429') || msg.includes('rate limit') || msg.includes('frequency') || msg.includes('ecosystem');
  };
  const getErrorCategory = (errorMessage) => {
    const mapped = translateMetaError(errorMessage);
    return mapped?.category || 'unknown';
  };

  const confirmRetry = (recipientIds) => {
    const campaignId = detailCampaignId;
    if (!campaignId) return;
    setRetryConfirmation({ campaignId, recipientIds });
  };

  const executeRetry = async () => {
    if (!retryConfirmation) return;
    const { campaignId, recipientIds } = retryConfirmation;
    setRetryConfirmation(null);
    setRetryingRecipients((prev) => { const next = new Set(prev); recipientIds.forEach((id) => next.add(id)); return next; });
    try {
      const response = await retryBroadcastRecipients(campaignId, recipientIds);
      const data = response?.data || {};
      if (data.retried > 0) {
        toast.success(language === 'ar' ? `تمت إعادة الإرسال لـ ${data.retried} مستلم` : `Retried ${data.retried} recipient(s) successfully`);
      }
      if (data.failed > 0) {
        toast.error(language === 'ar' ? `فشلت إعادة الإرسال لـ ${data.failed} مستلم` : `Retry failed for ${data.failed} recipient(s)`);
      }
      await loadRecipientReport(campaignId, false);
      loadCampaigns({ showLoading: false });
    } catch (e) {
      toast.error(e?.response?.data?.error || (language === 'ar' ? 'فشلت إعادة المحاولة' : 'Retry failed'));
    } finally {
      setRetryingRecipients(new Set());
    }
  };

  const copyTraceId = async (text) => {
    const t = String(text || '').trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      toast.success(language === 'ar' ? 'تم النسخ' : 'Copied');
    } catch (_) {
      toast.error(language === 'ar' ? 'تعذر النسخ' : 'Copy failed');
    }
  };

  const deliverySourceLabel = (src) => {
    const s = String(src || '').toLowerCase();
    if (s === 'webhook') return language === 'ar' ? 'ويب هوك ميتا' : 'Meta webhook';
    if (s === 'api_send') return language === 'ar' ? 'إرسال API' : 'API send';
    if (s === 'api_retry') return language === 'ar' ? 'إعادة محاولة' : 'Retry';
    if (s === 'backfill_snapshot') return language === 'ar' ? 'نسخة تاريخية' : 'Historical snapshot';
    return s || '—';
  };

  useEffect(() => {
    if (!timelineDrawer) {
      setTimelineState({ events: [], loading: false, error: '' });
      return undefined;
    }
    const { campaignId, recipientId } = timelineDrawer;
    let cancelled = false;
    setTimelineState({ events: [], loading: true, error: '' });
    getBroadcastDeliveryEvents(campaignId, { recipient_id: recipientId, limit: 500, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        const ev = Array.isArray(res?.data?.events) ? res.data.events : [];
        setTimelineState({ events: ev, loading: false, error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        setTimelineState({
          events: [],
          loading: false,
          error: e?.response?.data?.error || (language === 'ar' ? 'تعذر تحميل السجل' : 'Failed to load timeline')
        });
      });
    return () => { cancelled = true; };
  }, [timelineDrawer, language]);

  useEffect(() => {
    if (!detailCampaignId || campaignTraceTab !== 'delivery') return undefined;
    let cancelled = false;
    setDeliveryEventsLoading(true);
    setDeliveryEventsError('');
    getBroadcastDeliveryEvents(detailCampaignId, { limit: 1000, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        setDeliveryEvents(Array.isArray(res?.data?.events) ? res.data.events : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setDeliveryEventsError(e?.response?.data?.error || (language === 'ar' ? 'تعذر تحميل السجل' : 'Failed to load delivery log'));
        setDeliveryEvents([]);
      })
      .finally(() => { if (!cancelled) setDeliveryEventsLoading(false); });
    return () => { cancelled = true; };
  }, [detailCampaignId, campaignTraceTab, language]);

  useEffect(() => {
    if (!detailCampaignId || campaignTraceTab !== 'activity') return undefined;
    let cancelled = false;
    setAuditEntriesLoading(true);
    setAuditEntriesError('');
    getBroadcastCampaignAuditLog(detailCampaignId, { limit: 200 })
      .then((res) => {
        if (cancelled) return;
        setAuditEntries(Array.isArray(res?.data?.entries) ? res.data.entries : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setAuditEntriesError(e?.response?.data?.error || (language === 'ar' ? 'تعذر تحميل النشاط' : 'Failed to load activity'));
        setAuditEntries([]);
      })
      .finally(() => { if (!cancelled) setAuditEntriesLoading(false); });
    return () => { cancelled = true; };
  }, [detailCampaignId, campaignTraceTab, language]);

  const getCampaignStats = (campaign) => {
    const target = Number(campaign.total_recipients ?? campaign.target_count ?? 0);
    const sent = Number(campaign.sent_count || 0);
    const delivered = Number(campaign.delivered_count || 0);
    const read = Number(campaign.read_count || 0);
    const failed = Number(campaign.failed_count || 0);
    const pending = Math.max(0, target - sent - failed);
    return { target, sent, delivered, read, failed, pending };
  };

  const getCampaignStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'completed') return language === 'ar' ? 'مكتملة' : 'Completed';
    if (s === 'cancelled') return language === 'ar' ? 'ملغاة' : 'Cancelled';
    if (s === 'sending' || s === 'running') return language === 'ar' ? 'جارية' : 'Running';
    if (s === 'scheduled') return language === 'ar' ? 'مجدولة' : 'Scheduled';
    if (s === 'failed') return language === 'ar' ? 'فاشلة' : 'Failed';
    return language === 'ar' ? 'مسودة' : 'Draft';
  };

  const getRecipientModeLabel = (mode) => {
    const m = String(mode || 'auto').toLowerCase();
    if (m === 'manual_only') return language === 'ar' ? 'أرقام مكتوبة فقط' : 'Manual only';
    if (m === 'filters_only') return language === 'ar' ? 'فلاتر فقط' : 'Filters only';
    if (m === 'combined') return language === 'ar' ? 'فلاتر + أرقام مكتوبة' : 'Combined';
    return language === 'ar' ? 'تلقائي' : 'Auto';
  };

  const getActiveTargetFilterCount = (targetFilter = {}) => {
    const tf = targetFilter || {};
    let count = 0;
    if (tf.client_readiness) count += 1;
    if (tf.property_type) count += 1;
    if (tf.area) count += 1;
    if (tf.neighborhood) count += 1;
    const tagsCount = Array.isArray(tf.tags) ? tf.tags.length : (String(tf.tags || '').trim() ? 1 : 0);
    if (tagsCount > 0) count += 1;
    const manualCount = Array.isArray(tf.manual_numbers) ? tf.manual_numbers.length : 0;
    if (manualCount > 0) count += 1;
    return count;
  };

  const hasStructuredAudienceFilters = (targetFilter = {}) => {
    const tf = targetFilter || {};
    const tagsCount = Array.isArray(tf.tags) ? tf.tags.length : (String(tf.tags || '').trim() ? 1 : 0);
    return Boolean(tf.client_readiness || tf.property_type || tf.area || tf.neighborhood || tagsCount > 0);
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (statusFilter === 'all') return true;
    const status = String(campaign.status || '').toLowerCase();
    if (statusFilter === 'running') return status === 'running' || status === 'sending';
    return status === statusFilter;
  });

  const handleCreateCampaign = async () => {
    if (!campaignForm.name.trim() || !campaignForm.template_name.trim()) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    if (!audiencePreviewMeta.total) {
      toast.error(language === 'ar' ? 'لا يوجد مستلمون مطابقون حالياً. راجع الفلاتر أو الأرقام.' : 'No matching recipients found. Review filters or numbers.');
      return;
    }
    // Open inline confirm panel instead of window.confirm
    setConfirmSendOpen(true);
  };

  const handleCreateCampaignConfirmed = async () => {
    setConfirmSendOpen(false);
    try {
      await createBroadcastCampaign(campaignForm);
      setShowCreateForm(false);
      setShowFilters(false);
      setShowTemplatePreview(false);
      setCampaignForm({ name: '', template_name: '', scheduled_for: '', target_filter: { client_readiness: null, property_type: null, area: null, neighborhood: null, tags: [], manual_numbers: [], source_phone_number_id: null, recipient_mode: 'auto' } });
      setManualRecipientsInput('');
      setUploadedRecipientFileName('');
      setAudiencePreview(null);
      setAudiencePreviewList([]);
      setAudiencePreviewMeta({ mode: 'auto', total: 0, shown: 0 });
      loadCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleTestSend = async () => {
    const phone = testSendPhone.trim().replace(/\D/g, '');
    if (!phone || phone.length < 8) {
      toast.error(language === 'ar' ? 'يرجى إدخال رقم هاتف صحيح' : 'Please enter a valid phone number');
      return;
    }
    if (!campaignForm.template_name) {
      toast.error(language === 'ar' ? 'اختر قالباً أولاً' : 'Select a template first');
      return;
    }
    setTestSendStatus('sending');
    setTestSendMessage('');
    try {
      const integration = connectedIntegrations.find(
        (it) => it.phone_number_id === campaignForm.target_filter?.source_phone_number_id
      ) || connectedIntegrations[0];
      if (!integration) throw new Error(language === 'ar' ? 'لا يوجد خط واتساب متصل' : 'No connected WhatsApp line');
      await testSendWhatsAppTemplate(phone, campaignForm.template_name, integration.phone_number_id);
      setTestSendStatus('ok');
      setTestSendMessage(language === 'ar' ? `✅ تم إرسال الاختبار إلى +${phone}` : `✅ Test sent to +${phone}`);
    } catch (e) {
      setTestSendStatus('error');
      setTestSendMessage((language === 'ar' ? '❌ فشل الإرسال: ' : '❌ Failed: ') + (e?.response?.data?.error || e?.message || 'Unknown error'));
    }
  };

  const connectedIntegrations = availableIntegrations
    .filter((it) => String(it?.status || '').toLowerCase() === 'connected' && String(it?.phone_number_id || '').trim())
    .map((it) => ({
      phone_number_id: String(it.phone_number_id).trim(),
      label: (it.business_display_name && String(it.business_display_name).trim()) || String(it.phone_number_id).trim()
    }));

  const getIntegrationLabelById = (phoneNumberId) => {
    const id = String(phoneNumberId || '').trim();
    if (!id) return language === 'ar' ? 'الافتراضي' : 'Default';
    const match = connectedIntegrations.find((it) => it.phone_number_id === id);
    return match?.label || id;
  };

  useEffect(() => {
    if (!showCreateForm) return;
    if (connectedIntegrations.length !== 1) return;
    const onlyPhoneId = connectedIntegrations[0].phone_number_id;
    if (campaignForm.target_filter?.source_phone_number_id === onlyPhoneId) return;
    setCampaignForm((prev) => ({
      ...prev,
      target_filter: {
        ...(prev.target_filter || {}),
        source_phone_number_id: onlyPhoneId
      }
    }));
  }, [showCreateForm, connectedIntegrations, campaignForm.target_filter?.source_phone_number_id]);

  const loadRecipientReport = async (campaignId, resetFilter = true) => {
    setRecipientReportLoading(true);
    setRecipientReportError('');
    setRecipientReportRows([]);
    setRecipientReportSummary(null);
    setTargetedRecipients([]);
    if (resetFilter) setRecipientStatusFilter('all');
    try {
      const response = await getBroadcastCampaignRecipients(campaignId);
      const payload = response?.data || {};
      setRecipientReportSummary(payload.summary || null);
      setRecipientReportRows(Array.isArray(payload.recipients) ? payload.recipients : []);
      setTargetedRecipients(Array.isArray(payload.targeted_recipients) ? payload.targeted_recipients : []);
      setRecipientReportCampaignId(campaignId);
    } catch (error) {
      setRecipientReportError(error?.response?.data?.error || (language === 'ar' ? 'تعذر تحميل تقرير المستلمين' : 'Failed to load recipients report'));
    } finally {
      setRecipientReportLoading(false);
    }
  };

  const openRecipientDrilldown = async (campaignId, status = 'all') => {
    setRecipientSearch('');
    setDetailCampaignId(campaignId);
    if (recipientReportCampaignId === campaignId) {
      // Already loaded — just switch the filter tab
      setRecipientStatusFilter(status);
    } else {
      // Load with the desired filter active (don't reset to 'all')
      setRecipientStatusFilter(status);
      await loadRecipientReport(campaignId, false);
    }
  };

  // Auto-load recipient report whenever a campaign detail modal opens
  useEffect(() => {
    if (!detailCampaignId) return;
    if (recipientReportCampaignId === detailCampaignId) return; // already loaded
    loadRecipientReport(detailCampaignId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailCampaignId]);

  // Lock body scroll while modal is open — done in useEffect, never in render
  useEffect(() => {
    if (detailCampaignId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [detailCampaignId]);

  // No body scroll lock needed — the fixed backdrop handles it without freezing the UI

  // Auto-scroll modal body to recipient section when opened via a stat block click
  useEffect(() => {
    if (!detailCampaignId || !recipientSectionRef.current || !modalBodyRef.current) return;
    // Use offsetTop relative to the modal body so we scroll INSIDE the modal, not the page
    const t = setTimeout(() => {
      const body = modalBodyRef.current;
      const section = recipientSectionRef.current;
      if (!body || !section) return;
      const sectionTop = section.offsetTop;
      body.scrollTo({ top: sectionTop - 12, behavior: 'smooth' });
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailCampaignId, recipientStatusFilter]);

  const updateManualRecipientsFromInput = (rawText) => {
    const normalized = extractRecipientPhones(rawText);
    setCampaignForm((prev) => ({
      ...prev,
      target_filter: {
        ...(prev.target_filter || {}),
        manual_numbers: normalized,
        recipient_mode: normalized.length > 0 && !hasStructuredAudienceFilters(prev.target_filter || {}) ? 'manual_only' : (prev.target_filter?.recipient_mode || 'auto')
      }
    }));
  };

  const handleManualRecipientsChange = (value) => {
    setManualRecipientsInput(value);
    updateManualRecipientsFromInput(value);
  };

  const handleRecipientFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const fileNumbers = extractRecipientPhones(text);
      const currentNumbers = Array.isArray(campaignForm.target_filter?.manual_numbers) ? campaignForm.target_filter.manual_numbers : [];
      const merged = Array.from(new Set([...currentNumbers, ...fileNumbers]));
      setCampaignForm((prev) => ({
        ...prev,
        target_filter: {
          ...(prev.target_filter || {}),
          manual_numbers: merged,
          recipient_mode: merged.length > 0 && !hasStructuredAudienceFilters(prev.target_filter || {}) ? 'manual_only' : (prev.target_filter?.recipient_mode || 'auto')
        }
      }));
      setManualRecipientsInput(merged.join('\n'));
      setUploadedRecipientFileName(file.name);
      if (fileNumbers.length === 0) {
        toast.error(language === 'ar' ? 'لم يتم العثور على أرقام صالحة في الملف' : 'No valid phone numbers found in file');
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'تعذر قراءة الملف' : 'Failed to read file');
    } finally {
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="wa-loading">
        <div className="wa-loading-spinner" />
        <p className="wa-loading-text">
          {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
        </p>
      </div>
    );
  }

  if (campaignsError) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#b91c1c', marginBottom: '8px' }}>{campaignsError}</div>
        <button
          className="wa-btn wa-btn-primary"
          onClick={() => loadCampaigns()}
          style={{ marginTop: '8px', padding: '10px 24px' }}
        >
          {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div className="wa-animate-in" style={{ padding: '24px' }}>
      {/* Sub-tabs: Campaigns | Templates — hidden when only one is allowed */}
      {allowCampaignsSubTab && allowTemplatesSubTab && (
      <div className="wa-subtabs" style={{ display: 'flex', gap: '6px', marginBottom: '20px', padding: '4px', background: '#f1f5f9', borderRadius: '9999px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
        <button
          type="button"
          onClick={() => { setSubTab('campaigns'); onTabChange?.('campaigns'); }}
          className="wa-subtab"
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '9999px',
            background: subTab === 'campaigns' ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'transparent',
            color: subTab === 'campaigns' ? '#fff' : '#64748b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: subTab === 'campaigns' ? '0 2px 8px rgba(249,115,22,0.3)' : 'none'
          }}
          onMouseEnter={(e) => { if (subTab !== 'campaigns') e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.color = subTab === 'campaigns' ? '#fff' : '#ea580c'; }}
          onMouseLeave={(e) => { if (subTab !== 'campaigns') e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = subTab === 'campaigns' ? '#fff' : '#64748b'; }}
        >
          <FaBullhorn />
          {language === 'ar' ? 'الحملات' : 'Campaigns'}
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('templates'); onTabChange?.('templates'); }}
          className="wa-subtab"
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '9999px',
            background: subTab === 'templates' ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'transparent',
            color: subTab === 'templates' ? '#fff' : '#64748b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: subTab === 'templates' ? '0 2px 8px rgba(249,115,22,0.3)' : 'none'
          }}
          onMouseEnter={(e) => { if (subTab !== 'templates') e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.color = subTab === 'templates' ? '#fff' : '#ea580c'; }}
          onMouseLeave={(e) => { if (subTab !== 'templates') e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = subTab === 'templates' ? '#fff' : '#64748b'; }}
        >
          <FaFileAlt />
          {language === 'ar' ? 'القوالب' : 'Templates'}
        </button>
      </div>
      )}

      {subTab === 'templates' && allowTemplatesSubTab ? (
        <WhatsAppTemplates language={language} />
      ) : allowCampaignsSubTab ? (
        <>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#0f172a' }}>
          <FaBullhorn style={{ marginRight: '12px', color: '#ea580c' }} />
          {language === 'ar' ? 'حملات الواتساب' : 'WhatsApp Campaigns'}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {(() => {
            const mode = getRefreshMode();
            const modeLabel = mode === 'live'
              ? (language === 'ar' ? 'تحديث مباشر' : 'Live updates')
              : mode === 'recent'
                ? (language === 'ar' ? 'تحديث متابعة' : 'Recent updates')
                : (language === 'ar' ? 'تحديث يدوي' : 'Manual refresh');
            const dotColor = mode === 'live' ? '#f97316' : mode === 'recent' ? '#f59e0b' : '#94a3b8';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#64748b' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '9999px', background: dotColor }} />
                <span>{modeLabel}</span>
                {lastUpdatedAt && (
                  <span style={{ color: '#94a3b8' }}>
                    {language === 'ar' ? 'آخر تحديث' : 'Updated'}: {lastUpdatedAt.toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en')}
                  </span>
                )}
              </div>
            );
          })()}
          <button
            type="button"
            className="wa-btn wa-btn-secondary"
            onClick={async () => {
              setIsRefreshing(true);
              try {
                await loadCampaigns({ showLoading: false });
              } finally {
                setIsRefreshing(false);
              }
            }}
            style={{ height: '40px', padding: '0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            aria-label={language === 'ar' ? 'تحديث الحملات' : 'Refresh campaigns'}
            title={language === 'ar' ? 'تحديث الآن' : 'Refresh now'}
          >
            <FaSyncAlt style={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }} />
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </button>
          <button
            className={`wa-btn ${showCreateForm ? 'wa-btn-secondary' : 'wa-btn-primary'}`}
            style={{ height: '40px', padding: '0 20px', fontSize: '14px', transition: 'all 0.2s ease' }}
            onClick={() => {
              setShowCreateForm((prev) => !prev);
              if (showCreateForm) {
                setShowFilters(false);
                setManualRecipientsInput('');
                setUploadedRecipientFileName('');
              }
            }}
            aria-label={showCreateForm ? (language === 'ar' ? 'إلغاء وإغلاق النموذج' : 'Cancel and close form') : (language === 'ar' ? 'إنشاء حملة جديدة' : 'Create new campaign')}
          >
            {showCreateForm ? (language === 'ar' ? 'إلغاء' : 'Cancel') : (language === 'ar' ? '+ إنشاء حملة' : '+ Create Campaign')}
          </button>
        </div>
      </div>

      {/* Aggregate insights summary — this month */}
      {!showCreateForm && campaigns.length > 0 && (() => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthCampaigns = campaigns.filter((c) => {
          const d = new Date(c.created_at || c.started_at || 0);
          return d >= startOfMonth;
        });
        const all = campaigns;
        const totalTarget = all.reduce((s, c) => s + Number(c.total_recipients || 0), 0);
        const totalDelivered = all.reduce((s, c) => s + Number(c.delivered_count || 0), 0);
        const totalRead = all.reduce((s, c) => s + Number(c.read_count || 0), 0);
        const totalFailed = all.reduce((s, c) => s + Number(c.failed_count || 0), 0);
        const totalCampaigns = all.length;
        const completedCampaigns = all.filter((c) => String(c.status || '') === 'completed').length;
        // Calculate percentages: all percentages are based on totalTarget (total recipients across all campaigns)
        const deliveredPct = totalTarget > 0 ? Math.round((totalDelivered / totalTarget) * 100) : 0;
        const readPct = totalTarget > 0 ? Math.round((totalRead / totalTarget) * 100) : 0;
        const failedPct = totalTarget > 0 ? Math.round((totalFailed / totalTarget) * 100) : 0;
        const SUMMARY_ITEMS = [
          { labelAr: 'الحملات', labelEn: 'Campaigns', value: totalCampaigns, sub: `${completedCampaigns} ${language === 'ar' ? 'مكتملة' : 'completed'}`, color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
          { labelAr: 'نسبة التسليم', labelEn: 'Delivered', value: `${deliveredPct}%`, sub: `${totalDelivered.toLocaleString()} ${language === 'ar' ? 'من' : 'of'} ${totalTarget.toLocaleString()} ${language === 'ar' ? 'مستهدف' : 'target'}`, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { labelAr: 'نسبة القراءة', labelEn: 'Read', value: `${readPct}%`, sub: `${totalRead.toLocaleString()} ${language === 'ar' ? 'من' : 'of'} ${totalTarget.toLocaleString()} ${language === 'ar' ? 'مستهدف' : 'target'}`, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
          { labelAr: 'نسبة الفشل', labelEn: 'Failed', value: `${failedPct}%`, sub: `${totalFailed.toLocaleString()} ${language === 'ar' ? 'من' : 'of'} ${totalTarget.toLocaleString()} ${language === 'ar' ? 'مستهدف' : 'target'}`, color: totalFailed > 0 ? '#dc2626' : '#94a3b8', bg: totalFailed > 0 ? '#fef2f2' : '#f8fafc', border: totalFailed > 0 ? '#fecaca' : '#e2e8f0' },
        ];
        return (
          <div style={{ marginBottom: '24px', padding: '16px 20px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
              {language === 'ar' ? 'ملخص الأداء — كل الوقت' : 'Performance summary — all time'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {SUMMARY_ITEMS.map((s) => (
                <div key={s.labelEn} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '10px', padding: '12px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: s.color, marginTop: '3px' }}>{language === 'ar' ? s.labelAr : s.labelEn}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Inline Create Campaign Form */}
      {showCreateForm && (
        <div
          className="wa-animate-in"
          style={{
            marginBottom: '24px',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderLeft: '4px solid #ea580c',
            borderRadius: '12px',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '700', margin: '0 0 20px 0', color: '#0f172a' }}>
              {language === 'ar' ? 'إنشاء حملة جديدة' : 'Create New Campaign'}
            </h3>

            {/* Step 1: Name + Template + Schedule */}
            <div className="wa-campaign-form-grid" style={{ marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>
                  {language === 'ar' ? 'اسم الحملة' : 'Campaign Name'}
                </label>
                <input
                  type="text"
                  className="wa-input"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  placeholder={language === 'ar' ? 'مثال: حملة عروض الصيف' : 'e.g. Summer Offers'}
                  style={{ width: '100%', transition: 'border-color 0.2s' }}
                  aria-label={language === 'ar' ? 'اسم الحملة' : 'Campaign name'}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>
                  {language === 'ar' ? 'اسم القالب' : 'Template Name'}
                </label>
                <select
                  className="wa-input"
                  value={campaignForm.template_name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, template_name: e.target.value })}
                  style={{ width: '100%', transition: 'border-color 0.2s', cursor: 'pointer' }}
                  aria-label={language === 'ar' ? 'اختر قالب واتساب المعتمد' : 'Select approved WhatsApp template'}
                >
                  <option value="">{language === 'ar' ? 'اختر قالباً...' : 'Select template...'}</option>
                  {(metaTemplates.length > 0 ? metaTemplates : [{ name: 'hello_world', label_ar: 'مرحباً (افتراضي)', label_en: 'Hello (default)' }]).map((t) => (
                    <option key={t.name} value={t.name}>
                      {language === 'ar' ? (t.label_ar || t.name) : (t.label_en || t.name)}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {(language === 'ar' ? 'يجب أن يكون القالب معتمداً من ميتا' : 'Template must be approved by Meta')}
                  {allowTemplatesSubTab && (
                  <button type="button" onClick={() => { setSubTab('templates'); onTabChange?.('templates'); }} style={{ fontSize: '11px', color: '#ea580c', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      {language === 'ar' ? 'إدارة القوالب' : 'Manage templates'}
                  </button>
                  )}
                </p>
                {campaignForm.template_name && (() => {
                  const t = metaTemplates.find((m) => m.name === campaignForm.template_name);
                  if (!t) return null;
                  const comps = t.meta_components || t.components || [];
                  const body = t.body_text || comps.find((c) => c.type === 'BODY')?.text || (t.name === 'hello_world' ? 'Hello World' : '');
                  const btns = comps.find((c) => c.type === 'BUTTONS')?.buttons || [];
                  const b = btns[0];
                  let btnType = '';
                  if (b?.type === 'URL') btnType = 'url';
                  else if (b?.type === 'PHONE_NUMBER') btnType = 'phone';
                  else if (b?.type === 'QUICK_REPLY') btnType = 'quick_reply';
                  const headerComp = comps.find((c) => c.type === 'HEADER');
                  const headerFormat = headerComp?.format || '';
                  const headerMediaUrl = headerComp?.example?.header_handle?.[0] || headerComp?.example?.header_url?.[0] || '';
                  const headerText = headerComp?.text || '';
                  return (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setShowTemplatePreview((v) => !v)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: '#ea580c', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
                      >
                        {showTemplatePreview ? '▲' : '▶'} {language === 'ar' ? 'معاينة القالب' : 'Preview template'}
                      </button>
                      {showTemplatePreview && (
                        <div style={{ marginTop: '12px', padding: '20px 24px', background: '#fef7ed', border: '1px solid #fdba74', borderRadius: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <WhatsAppTemplatePreview
                              bodyText={body}
                              headerFormat={headerFormat}
                              headerText={headerText}
                              headerMediaUrl={headerMediaUrl}
                              buttonType={btnType}
                              buttonUrlText={btns.find((b) => b.type === 'URL')?.text || ''}
                              buttonUrl={btns.find((b) => b.type === 'URL')?.url || ''}
                              buttonPhoneText={btns.find((b) => b.type === 'PHONE_NUMBER')?.text || ''}
                              buttonPhoneNumber={btns.find((b) => b.type === 'PHONE_NUMBER')?.phone_number || ''}
                              buttonQuick={btns.filter((b) => b.type === 'QUICK_REPLY').map((b) => b.text) || []}
                              footerText={comps.find((c) => c.type === 'FOOTER')?.text || ''}
                              language={language}
                              showSampleSelector={true}
                              phoneFrame={true}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
        </div>
        <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>
                  {language === 'ar' ? 'جدولة الإرسال (اختياري)' : 'Schedule send (optional)'}
                </label>
                <input
                  type="datetime-local"
                  className="wa-input"
                  value={campaignForm.scheduled_for || ''}
                  onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_for: e.target.value || '' })}
                  min={new Date().toISOString().slice(0, 16)}
                  style={{ width: '100%', transition: 'border-color 0.2s' }}
                  aria-label={language === 'ar' ? 'جدولة الإرسال' : 'Schedule send'}
                />
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                  {language === 'ar' ? 'اتركه فارغاً للإرسال يدوياً. يُفضّل الصباح أو بداية المساء.' : 'Leave empty to send manually. Prefer morning or early evening.'}
                </p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>
                  {language === 'ar' ? 'رقم الإرسال (الخط)' : 'Sending number (line)'}
                </label>
                <select
                  className="wa-input"
                  value={campaignForm.target_filter?.source_phone_number_id || ''}
                  onChange={(e) => setCampaignForm({
                    ...campaignForm,
                    target_filter: {
                      ...campaignForm.target_filter,
                      source_phone_number_id: e.target.value || null
                    }
                  })}
                  style={{ width: '100%', transition: 'border-color 0.2s', cursor: 'pointer' }}
                >
                  <option value="">{language === 'ar' ? 'الافتراضي (المتصل)' : 'Default connected line'}</option>
                  {connectedIntegrations.map((it) => (
                    <option key={it.phone_number_id} value={it.phone_number_id}>
                      {it.label}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                  {language === 'ar'
                    ? 'اختيار رقم محدد يضمن الإرسال من الخط المطلوب حتى مع تعدد الأرقام.'
                    : 'Selecting a specific line guarantees campaign delivery from that exact number.'}
                </p>
              </div>
            </div>

            {/* Step 2: Recipients — always visible, primary input method */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px', marginTop: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#ea580c', color: '#fff', fontSize: '12px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</span>
                {language === 'ar' ? 'المستلمون' : 'Recipients'}
              </div>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#64748b' }}>
                {language === 'ar'
                  ? 'ألصق الأرقام (رقم في كل سطر) أو ارفع ملف CSV/TXT — أو استخدم فلاتر CRM أدناه.'
                  : 'Paste numbers (one per line) or upload a CSV/TXT file — or use CRM filters below.'}
              </p>
              <textarea
                className="wa-input"
                value={manualRecipientsInput}
                onChange={(e) => handleManualRecipientsChange(e.target.value)}
                placeholder={language === 'ar' ? '9665xxxxxxx\n9665yyyyyyy\n...' : '9665xxxxxxx\n9665yyyyyyy\n...'}
                rows={4}
                style={{ width: '100%', resize: 'vertical', fontSize: '13px', lineHeight: 1.6 }}
              />
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="wa-btn wa-btn-secondary" style={{ cursor: 'pointer', padding: '8px 14px', fontSize: '12px' }}>
                  📎 {language === 'ar' ? 'رفع ملف (CSV / TXT)' : 'Upload file (CSV / TXT)'}
                  <input type="file" accept=".csv,.txt,.tsv" onChange={handleRecipientFileUpload} style={{ display: 'none' }} />
                </label>
                {uploadedRecipientFileName && (
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    ✅ {uploadedRecipientFileName}
                  </span>
                )}
                {(campaignForm.target_filter?.manual_numbers || []).length > 0 && (
                  <span style={{ fontSize: '12px', color: '#0f766e', fontWeight: '700', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '9999px', padding: '3px 10px' }}>
                    {(campaignForm.target_filter?.manual_numbers || []).length} {language === 'ar' ? 'رقم' : 'numbers'}
                  </span>
                )}
              </div>
            </div>

            {/* Step 3: CRM Filters — secondary, collapsed by default */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: '16px' }}>
            <button 
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
                aria-label={language === 'ar' ? (showFilters ? 'إخفاء فلاتر الجمهور' : 'إظهار فلاتر الجمهور') : (showFilters ? 'Hide audience filters' : 'Show audience filters')}
              style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  marginLeft: '-14px',
                  marginRight: '-14px',
                border: 'none',
                  borderRadius: '9999px',
                  background: showFilters ? '#f1f5f9' : 'none',
                cursor: 'pointer',
                  fontSize: '14px',
                fontWeight: '600',
                  color: showFilters ? '#0f172a' : '#64748b',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0f172a';
                  e.currentTarget.style.background = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = showFilters ? '#0f172a' : '#64748b';
                  e.currentTarget.style.background = showFilters ? '#f1f5f9' : 'transparent';
                }}
              >
                <FaFilter style={{ fontSize: '14px' }} />
                {language === 'ar' ? 'استهداف جمهور محدد' : 'Target specific audience'}
                {(() => {
                  const n = getActiveTargetFilterCount(campaignForm.target_filter);
                  return n > 0 ? <span style={{ padding: '2px 8px', background: '#f97316', color: '#fff', borderRadius: '9999px', fontSize: '11px', fontWeight: '700' }}>{n}</span> : null;
                })()}
                {showFilters ? <FaChevronUp style={{ marginLeft: 'auto' }} /> : <FaChevronDown style={{ marginLeft: 'auto' }} />}
              </button>
              {!showFilters && getActiveTargetFilterCount(campaignForm.target_filter) === 0 && (
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                  {language === 'ar' ? 'سيتم الإرسال لجميع المحادثات' : 'Will send to all conversations'}
                </p>
              )}
              {audiencePreview !== null && (
                <p style={{ margin: '12px 0 0 0', fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
                  {language === 'ar' ? `عدد المستلمين المتوقع: ${audiencePreview}` : `Estimated recipients: ${audiencePreview}`}
                </p>
              )}
              {audiencePreviewMeta.total > 0 && (
                <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}>
                    {language === 'ar' ? 'مراجعة المستلمين قبل الإرسال' : 'Recipients review before sending'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#475569', marginBottom: '8px' }}>
                    {language === 'ar'
                      ? `الوضع: ${getRecipientModeLabel(audiencePreviewMeta.mode)} • المعروض ${audiencePreviewMeta.shown} من ${audiencePreviewMeta.total}`
                      : `Mode: ${getRecipientModeLabel(audiencePreviewMeta.mode)} • Showing ${audiencePreviewMeta.shown} of ${audiencePreviewMeta.total}`}
                  </div>
                  <div style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                    {audiencePreviewList.map((item) => (
                      <div key={`${item.phone}-${item.source}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                        <span style={{ color: '#0f172a', fontWeight: '600' }}>{item.phone}</span>
                        <span style={{ color: '#64748b' }}>
                          {item.source === 'manual'
                            ? (language === 'ar' ? 'يدوي' : 'manual')
                            : (language === 'ar' ? 'فلاتر' : 'filters')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {showFilters && (
                <>
                  <div className="wa-campaign-filters-grid" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                    {/* Row 1: Lead + Property — improved dropdowns */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>{language === 'ar' ? 'جاهزية العميل' : 'Client readiness'}</label>
                        <select className="wa-input" value={campaignForm.target_filter?.client_readiness || ''} onChange={(e) => setCampaignForm({ ...campaignForm, target_filter: { ...campaignForm.target_filter, client_readiness: e.target.value || null } })} style={{ width: '100%', padding: '12px 14px', fontSize: '14px', cursor: 'pointer', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500' }} aria-label={language === 'ar' ? 'جاهزية العميل' : 'Client readiness'}>
                          <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                          <option value="high">{language === 'ar' ? 'عالية' : 'High'}</option>
                          <option value="medium">{language === 'ar' ? 'متوسطة' : 'Medium'}</option>
                          <option value="low">{language === 'ar' ? 'منخفضة' : 'Low'}</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>{language === 'ar' ? 'نوع العقار' : 'Property type'}</label>
                        <select className="wa-input" value={campaignForm.target_filter?.property_type || ''} onChange={(e) => setCampaignForm({ ...campaignForm, target_filter: { ...campaignForm.target_filter, property_type: e.target.value || null } })} style={{ width: '100%', padding: '12px 14px', fontSize: '14px', cursor: 'pointer', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500' }} aria-label={language === 'ar' ? 'نوع العقار' : 'Property type'}>
                          <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                          <option value="apartment">{language === 'ar' ? 'شقة' : 'Apartment'}</option>
                          <option value="villa">{language === 'ar' ? 'فيلا' : 'Villa'}</option>
                          <option value="land">{language === 'ar' ? 'أرض' : 'Land'}</option>
                          <option value="commercial">{language === 'ar' ? 'تجاري' : 'Commercial'}</option>
                        </select>
                      </div>
                    </div>
                    {/* Row 2: Location — Area and Neighborhood in separate, clearly labeled sections */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {language === 'ar' ? '📍 الموقع الجغرافي' : '📍 Location'}
                      </div>
                      {/* Area — separate block */}
                      <div style={{ padding: '14px 16px', background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '12px', borderLeft: '4px solid #f97316' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                          {language === 'ar' ? 'المنطقة' : 'Area'}
                        </label>
                        <select className="wa-input" value={campaignForm.target_filter?.area || ''} onChange={(e) => {
                          const area = e.target.value || null;
                          const hoods = area ? (NEIGHBORHOOD_BY_AREA[area] || []) : [];
                          const curNeighborhood = campaignForm.target_filter?.neighborhood;
                          const validNeighborhood = curNeighborhood && hoods.some((h) => h.value === curNeighborhood) ? curNeighborhood : null;
                          setCampaignForm({ ...campaignForm, target_filter: { ...campaignForm.target_filter, area, neighborhood: validNeighborhood } });
                        }} style={{ width: '100%', padding: '12px 14px', fontSize: '14px', cursor: 'pointer', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500' }} aria-label={language === 'ar' ? 'المنطقة' : 'Area'}>
                          <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                          {AREA_GROUPS.map((g) => (
                            <optgroup key={g.groupAr} label={language === 'ar' ? g.groupAr : g.groupEn}>
                              {g.options.filter(a => a.value).map((a) => (
                                <option key={a.value} value={a.value}>{language === 'ar' ? a.labelAr : a.labelEn}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{language === 'ar' ? 'المدينة أو المنطقة الكبرى (مثال: الرياض، جدة)' : 'City or major region (e.g. Riyadh, Jeddah)'}</p>
                      </div>
                      {/* Neighborhood — separate block, depends on Area */}
                      <div style={{ padding: '14px 16px', background: campaignForm.target_filter?.area ? '#fff' : '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', opacity: campaignForm.target_filter?.area ? 1 : 0.85 }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                          {language === 'ar' ? 'الحي' : 'Neighborhood'}
                        </label>
                        <select className="wa-input" value={campaignForm.target_filter?.neighborhood || ''} onChange={(e) => setCampaignForm({ ...campaignForm, target_filter: { ...campaignForm.target_filter, neighborhood: e.target.value || null } })} style={{ width: '100%', padding: '12px 14px', fontSize: '14px', cursor: campaignForm.target_filter?.area ? 'pointer' : 'not-allowed', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500' }} aria-label={language === 'ar' ? 'الحي' : 'Neighborhood'} disabled={!campaignForm.target_filter?.area}>
                          <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                          {(NEIGHBORHOOD_BY_AREA[campaignForm.target_filter?.area] || []).map((n) => (
                            <option key={n.value} value={n.value}>{language === 'ar' ? n.labelAr : n.labelEn}</option>
                          ))}
                        </select>
                        {!campaignForm.target_filter?.area ? (
                          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', fontStyle: 'italic' }}>{language === 'ar' ? 'اختر المنطقة أولاً لتفعيل الحي' : 'Select area first to enable neighborhood'}</p>
                        ) : (NEIGHBORHOOD_BY_AREA[campaignForm.target_filter?.area] || []).length === 0 ? (
                          <p style={{ fontSize: '11px', color: '#f59e0b', marginTop: '6px', fontStyle: 'italic' }}>{language === 'ar' ? 'لا تتوفر أحياء مسجّلة لهذه المدينة حالياً' : 'No neighborhoods available for this city yet'}</p>
                        ) : (
                          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{language === 'ar' ? 'الحي السكني داخل المدينة المختارة' : 'District within selected city'}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#64748b' }}>{language === 'ar' ? 'التصنيفات' : 'Tags'}</label>
                      <input type="text" className="wa-input" value={Array.isArray(campaignForm.target_filter?.tags) ? campaignForm.target_filter.tags.join(', ') : (campaignForm.target_filter?.tags || '')} onChange={(e) => { const v = e.target.value; const arr = v ? v.split(/[,،]/).map((s) => s.trim()).filter(Boolean) : []; setCampaignForm({ ...campaignForm, target_filter: { ...campaignForm.target_filter, tags: arr } }); }} placeholder={language === 'ar' ? 'vip، مستثمر' : 'vip, investor'} style={{ width: '100%', padding: '10px 12px', fontSize: '13px' }} aria-label={language === 'ar' ? 'التصنيفات' : 'Tags'} />
                    </div>
                    <div style={{ marginTop: '14px', padding: '14px 16px', background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', marginBottom: '6px', color: '#0f172a' }}>
                          {language === 'ar' ? 'طريقة تحديد المستلمين' : 'Recipient selection mode'}
                        </label>
                        <select
                          className="wa-input"
                          value={campaignForm.target_filter?.recipient_mode || 'auto'}
                          onChange={(e) => setCampaignForm({
                            ...campaignForm,
                            target_filter: {
                              ...campaignForm.target_filter,
                              recipient_mode: e.target.value || 'auto'
                            }
                          })}
                          style={{ width: '100%', padding: '10px 12px', fontSize: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                        >
                          <option value="auto">{language === 'ar' ? 'تلقائي (الموصى به)' : 'Auto (recommended)'}</option>
                          <option value="manual_only">{language === 'ar' ? 'الأرقام المكتوبة فقط' : 'Manual numbers only'}</option>
                          <option value="filters_only">{language === 'ar' ? 'الفلاتر فقط' : 'Filters only'}</option>
                          <option value="combined">{language === 'ar' ? 'الفلاتر + الأرقام المكتوبة' : 'Filters + manual numbers'}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '11px', color: '#94a3b8' }}>{language === 'ar' ? '💡 ابدأ بالجاهزية ثم المدينة ثم نوع العقار. الحملات الأصغر والأكثر تركيزاً عادةً تعطي نتائج أفضل.' : '💡 Start with lead, then city, then property type. Smaller, focused campaigns usually perform better.'}</div>
                  <div style={{ marginTop: '16px', padding: '14px 18px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '1px solid #bae6fd', borderRadius: '12px', fontSize: '13px', color: '#0369a1', boxShadow: '0 1px 3px rgba(6, 182, 212, 0.08)' }}>
                    <div style={{ fontWeight: '700', marginBottom: '8px', fontSize: '14px' }}>{language === 'ar' ? 'معاينة الجمهور' : 'Audience Preview'}</div>
                    {(() => {
                      const tf = campaignForm.target_filter || {};
                      const active = Object.entries(tf).filter(([k, v]) => {
                        if (k === 'source_phone_number_id' || k === 'recipient_mode') return false;
                        if (k === 'tags') return Array.isArray(v) ? v.length > 0 : !!String(v || '').trim();
                        if (k === 'manual_numbers') return Array.isArray(v) && v.length > 0;
                        return !!v;
                      });
                      if (active.length === 0) return language === 'ar' ? 'سيتم الإرسال لجميع المحادثات' : 'Will send to all conversations';
                      const labels = { client_readiness: language === 'ar' ? 'جاهزية العميل' : 'Client Readiness', property_type: language === 'ar' ? 'النوع' : 'Property', area: language === 'ar' ? 'المنطقة' : 'Area', neighborhood: language === 'ar' ? 'الحي' : 'Neighborhood', tags: language === 'ar' ? 'التصنيفات' : 'Tags', manual_numbers: language === 'ar' ? 'أرقام مكتوبة' : 'Manual numbers' };
                      return (language === 'ar' ? 'الفلاتر النشطة' : 'Active filters') + ': ' + active.map(([k]) => labels[k] || k).join(' • ');
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Test send section */}
          <div style={{ padding: '14px 28px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>
              🧪 {language === 'ar' ? 'اختبار قبل الإرسال (اختياري)' : 'Test before sending (optional)'}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="tel"
                value={testSendPhone}
                onChange={(e) => { setTestSendPhone(e.target.value); setTestSendStatus(null); setTestSendMessage(''); }}
                placeholder={language === 'ar' ? 'رقم الاختبار (مثال: 9665xxxxxxx)' : 'Test number (e.g. 9665xxxxxxx)'}
                style={{ flex: '1', minWidth: '200px', padding: '8px 12px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none' }}
              />
              <button
                type="button"
                className="wa-btn wa-btn-secondary"
                onClick={handleTestSend}
                disabled={testSendStatus === 'sending' || !campaignForm.template_name}
                style={{ padding: '8px 16px', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                {testSendStatus === 'sending' ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending…') : (language === 'ar' ? 'إرسال اختبار' : 'Send test')}
              </button>
            </div>
            {testSendMessage && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: testSendStatus === 'ok' ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                {testSendMessage}
              </div>
            )}
          </div>

          {/* Inline confirm panel — replaces window.confirm */}
          {confirmSendOpen ? (
            <div style={{ padding: '16px 28px', background: '#fff7ed', borderTop: '2px solid #fdba74', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#9a3412' }}>
                {language === 'ar'
                  ? `⚠️ سيتم إرسال الحملة إلى ${audiencePreviewMeta.total} مستلم — هل أنت متأكد؟`
                  : `⚠️ This will send to ${audiencePreviewMeta.total} recipients — are you sure?`}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="wa-btn wa-btn-primary" onClick={handleCreateCampaignConfirmed} style={{ padding: '9px 22px', background: '#ea580c', borderColor: '#ea580c' }}>
                  {language === 'ar' ? 'نعم، إنشاء الحملة' : 'Yes, create campaign'}
                </button>
                <button className="wa-btn wa-btn-secondary" onClick={() => setConfirmSendOpen(false)} style={{ padding: '9px 18px' }}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: '16px 28px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                className="wa-btn wa-btn-secondary"
                onClick={() => { setShowCreateForm(false); setShowFilters(false); setShowTemplatePreview(false); setConfirmSendOpen(false); setTestSendStatus(null); setTestSendMessage(''); setManualRecipientsInput(''); setUploadedRecipientFileName(''); setAudiencePreview(null); setAudiencePreviewList([]); setAudiencePreviewMeta({ mode: 'auto', total: 0, shown: 0 }); }}
                style={{ padding: '10px 20px' }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button className="wa-btn wa-btn-primary" onClick={handleCreateCampaign} style={{ padding: '10px 24px' }}>
                {language === 'ar' ? 'إنشاء الحملة ←' : 'Create Campaign →'}
              </button>
            </div>
          )}
        </div>
      )}

      {campaigns.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {[
            { key: 'all', ar: 'الكل', en: 'All' },
            { key: 'running', ar: 'جارية', en: 'Running' },
            { key: 'completed', ar: 'مكتملة', en: 'Completed' },
            { key: 'scheduled', ar: 'مجدولة', en: 'Scheduled' },
            { key: 'draft', ar: 'مسودة', en: 'Draft' },
            { key: 'cancelled', ar: 'ملغاة', en: 'Cancelled' }
          ].map((item) => {
            const count = item.key === 'all'
              ? campaigns.length
              : item.key === 'running'
                ? campaigns.filter((c) => ['running', 'sending'].includes(String(c.status || '').toLowerCase())).length
                : campaigns.filter((c) => String(c.status || '').toLowerCase() === item.key).length;
            const active = statusFilter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setStatusFilter(item.key)}
                style={{
                  height: '30px',
                  padding: '0 10px',
                  borderRadius: '9999px',
                  border: active ? '1px solid #fb923c' : '1px solid #e2e8f0',
                  background: active ? '#fff7ed' : '#fff',
                  color: active ? '#c2410c' : '#475569',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>{language === 'ar' ? item.ar : item.en}</span>
                <span style={{ minWidth: '16px', textAlign: 'center', color: active ? '#9a3412' : '#64748b' }}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {campaigns.length === 0 && !showCreateForm ? (
        <div className="wa-empty-state">
          <div className="wa-empty-state-icon">
            <FaBullhorn />
          </div>
          <p>{language === 'ar' ? 'لا توجد حملات بعد' : 'No campaigns yet'}</p>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
            {language === 'ar' 
              ? 'أنشئ حملة لإرسال رسائل جماعية للعملاء'
              : 'Create a campaign to send bulk messages to clients'}
          </p>
        </div>
      ) : filteredCampaigns.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
          {filteredCampaigns.map((campaign) => (
            <div 
              key={campaign.id}
              className="wa-metric-card"
              onMouseEnter={() => setHoveredCampaignId(campaign.id)}
              onMouseLeave={() => setHoveredCampaignId(null)}
              style={{
                padding: '14px',
                borderLeft: `4px solid ${
                  campaign.status === 'completed' ? '#16a34a' :
                  campaign.status === 'sending' || campaign.status === 'running' ? '#fb923c' :
                  campaign.status === 'scheduled' ? '#f59e0b' :
                  campaign.status === 'cancelled' ? '#94a3b8' :
                  campaign.status === 'failed' ? '#ef4444' : '#e2e8f0'
                }`,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: hoveredCampaignId === campaign.id
                  ? '0 8px 18px rgba(234, 88, 12, 0.12)'
                  : '0 2px 6px rgba(15, 23, 42, 0.04)',
                transform: hoveredCampaignId === campaign.id ? 'translateY(-1px)' : 'translateY(0)',
                transition: 'box-shadow 0.2s ease, transform 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span
                      style={{
                        fontWeight: '600',
                        fontSize: '16px',
                        color: '#0f172a',
                        cursor: onOpenCampaignReport ? 'pointer' : 'default',
                        textDecoration: onOpenCampaignReport && hoveredCampaignId === campaign.id ? 'underline' : 'none',
                        textUnderlineOffset: '3px'
                      }}
                      onClick={onOpenCampaignReport ? () => onOpenCampaignReport(campaign.id) : undefined}
                      title={onOpenCampaignReport ? (language === 'ar' ? 'فتح تقرير الحملة' : 'Open campaign report') : undefined}
                    >
                      {campaign.name}
                    </span>
                    <button type="button" onClick={() => setDetailCampaignId(campaign.id)} style={{ padding: '2px', border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }} title={language === 'ar' ? 'عرض التفاصيل' : 'View details'}>
                      <FaInfoCircle style={{ fontSize: '14px' }} />
                    </button>
                    {onOpenCampaignReport && (
                      <button
                        type="button"
                        onClick={() => onOpenCampaignReport(campaign.id)}
                        style={{
                          padding: '2px 8px',
                          border: hoveredCampaignId === campaign.id ? '1px solid #fb923c' : '1px solid #fdba74',
                          background: hoveredCampaignId === campaign.id ? '#f97316' : '#fff7ed',
                          borderRadius: '9999px',
                          cursor: 'pointer',
                          color: hoveredCampaignId === campaign.id ? '#ffffff' : '#c2410c',
                          fontSize: '11px',
                          fontWeight: '700',
                          boxShadow: hoveredCampaignId === campaign.id ? '0 2px 8px rgba(249, 115, 22, 0.35)' : 'none',
                          transition: 'all 0.2s ease'
                        }}
                        title={language === 'ar' ? 'عرض تقرير الحملة' : 'Open campaign report'}
                      >
                        {language === 'ar' ? 'التقرير' : 'Report'}
                      </button>
                    )}
                    {isManagerOrOwner && (
                      <button
                        type="button"
                        onClick={() => setExpandedManagerCampaignId((prev) => prev === campaign.id ? null : campaign.id)}
                        style={{
                          padding: '2px 8px',
                          border: '1px solid #fed7aa',
                          background: expandedManagerCampaignId === campaign.id ? '#fff7ed' : '#ffffff',
                          borderRadius: '9999px',
                          cursor: 'pointer',
                          color: '#9a3412',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}
                        title={language === 'ar' ? 'تفاصيل إضافية للمدير/المالك' : 'More details for manager/owner'}
                      >
                        {expandedManagerCampaignId === campaign.id
                          ? (language === 'ar' ? 'إخفاء التفاصيل' : 'Hide details')
                          : (language === 'ar' ? 'تفاصيل إضافية' : 'More details')}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    {language === 'ar' ? 'القالب' : 'Template'}: {campaign.template_name}
                  </div>
                  {campaign.status === 'scheduled' && campaign.scheduled_for && (
                    <div style={{ fontSize: '12px', color: '#b45309', marginTop: '4px' }}>
                      {language === 'ar' ? 'مجدولة لـ' : 'Scheduled for'}: {new Date(campaign.scheduled_for).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {(campaign.status === 'draft' || campaign.status === 'scheduled') && editingScheduleId === campaign.id ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <input
                        type="datetime-local"
                        className="wa-input"
                        value={editingScheduleValue}
                        onChange={(e) => setEditingScheduleValue(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        style={{ minWidth: '200px', fontSize: '13px', padding: '8px 12px' }}
                      />
                      <button
                        className="wa-btn wa-btn-primary"
                        style={{ padding: '8px 14px', fontSize: '13px' }}
                        onClick={async () => {
                          try {
                            await updateBroadcastCampaign(campaign.id, { scheduled_for: editingScheduleValue || null });
                            setEditingScheduleId(null);
                            setEditingScheduleValue('');
                            loadCampaigns();
                          } catch (e) {
                            toast.error(e?.response?.data?.error || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
                          }
                        }}
                      >
                        {language === 'ar' ? 'حفظ' : 'Save'}
                      </button>
                      <button
                        className="wa-btn wa-btn-secondary"
                        style={{ padding: '8px 14px', fontSize: '13px' }}
                        onClick={() => { setEditingScheduleId(null); setEditingScheduleValue(''); }}
                      >
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                      </button>
                    </div>
                  ) : (campaign.status === 'draft' || campaign.status === 'scheduled') && (
                    <>
                      <button
                        type="button"
                        className="wa-btn wa-btn-secondary"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => {
                          setEditingScheduleId(campaign.id);
                          const d = campaign.scheduled_for ? new Date(campaign.scheduled_for) : new Date();
                          d.setMinutes(d.getMinutes() + 30);
                          const pad = (n) => String(n).padStart(2, '0');
                          setEditingScheduleValue(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                        }}
                        title={language === 'ar' ? 'اختر موعد الإرسال التلقائي' : 'Choose when to send automatically'}
                      >
                        <FaCalendarAlt style={{ fontSize: '12px' }} />
                        {campaign.scheduled_for ? (language === 'ar' ? 'تغيير الموعد' : 'Change schedule') : (language === 'ar' ? 'جدولة الإرسال' : 'Schedule send')}
                      </button>
                      {campaign.scheduled_for && (
                        <button
                          type="button"
                          className="wa-btn wa-btn-secondary"
                          style={{ padding: '8px 14px', fontSize: '13px' }}
                          onClick={async () => {
                            try {
                              await updateBroadcastCampaign(campaign.id, { scheduled_for: null });
                              loadCampaigns();
                            } catch (e) {
                              toast.error(e?.response?.data?.error || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
                            }
                          }}
                        >
                          {language === 'ar' ? 'إلغاء الجدولة' : 'Clear schedule'}
                        </button>
                      )}
                      <button
                        className="wa-btn wa-btn-primary"
                        disabled={!!runningCampaignId}
                        onClick={async () => {
                          let previewTotal = 0;
                          let previewSample = [];
                          try {
                            const rawFilter = typeof campaign.target_filter === 'string'
                              ? JSON.parse(campaign.target_filter || '{}')
                              : (campaign.target_filter || {});
                            const previewRes = await getBroadcastPreviewList(rawFilter, 5);
                            previewTotal = Number(previewRes?.data?.total || 0);
                            previewSample = (previewRes?.data?.recipients || []).map((r) => r.phone).filter(Boolean).slice(0, 3);
                          } catch (_) {
                            previewTotal = Number(campaign.total_recipients || campaign.target_count || 0);
                            previewSample = [];
                          }
                          if (!previewTotal) {
                            toast.error(language === 'ar' ? 'لا يوجد مستلمون مطابقون لهذه الحملة.' : 'No matching recipients for this campaign.');
                            return;
                          }
                          const sampleText = previewSample.length > 0 ? `\n${previewSample.join(', ')}` : '';
                          const ok = window.confirm(
                            language === 'ar'
                              ? `سيتم إرسال الحملة إلى ${previewTotal} مستلم.${sampleText}\nهل تريد المتابعة؟`
                              : `This campaign will send to ${previewTotal} recipients.${sampleText}\nContinue?`
                          );
                          if (!ok) return;
                          setRunningCampaignId(campaign.id);
                          try {
                            await runBroadcastCampaign(campaign.id);
                            loadCampaigns();
                          } catch (e) {
                            toast.error(e?.response?.data?.error || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
                          } finally {
                            setRunningCampaignId(null);
                          }
                        }}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                        aria-label={language === 'ar' ? `إرسال الحملة ${campaign.name}` : `Send campaign ${campaign.name}`}
                      >
                        {runningCampaignId === campaign.id ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال الآن' : 'Send now')}
                      </button>
                      <button
                        type="button"
                        className="wa-btn wa-btn-secondary"
                        disabled={!!runningCampaignId}
                        onClick={async () => {
                          if (!window.confirm(language === 'ar' ? 'هل تريد إلغاء هذه الحملة نهائياً؟ لن يتم الإرسال.' : 'Cancel this campaign permanently? It will not be sent.')) return;
                          try {
                            await cancelBroadcastCampaign(campaign.id);
                            loadCampaigns();
                          } catch (e) {
                            const msg = e?.response?.data?.error || e?.message || (language === 'ar' ? 'حدث خطأ' : 'An error occurred');
                            toast.error(msg);
                          }
                        }}
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', borderColor: '#dc2626', color: '#dc2626' }}
                        title={language === 'ar' ? 'إلغاء الحملة ولن يتم الإرسال' : 'Cancel campaign - it will not be sent'}
                        aria-label={language === 'ar' ? `إلغاء الحملة ${campaign.name}` : `Cancel campaign ${campaign.name}`}
                      >
                        <FaTimes style={{ fontSize: '12px' }} />
                        {language === 'ar' ? 'إلغاء الحملة' : 'Cancel campaign'}
                      </button>
                    </>
                  )}
                  <span style={{ 
                    padding: '4px 12px', 
                    borderRadius: '9999px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    background: campaign.status === 'completed' ? '#f0fdf4' : 
                               campaign.status === 'cancelled' ? '#f1f5f9' :
                               campaign.status === 'sending' || campaign.status === 'running' ? '#ffedd5' : 
                               campaign.status === 'scheduled' ? '#fef3c7' : '#f1f5f9',
                    color: campaign.status === 'completed' ? '#166534' : 
                           campaign.status === 'cancelled' ? '#94a3b8' :
                           campaign.status === 'sending' || campaign.status === 'running' ? '#c2410c' : 
                           campaign.status === 'scheduled' ? '#b45309' : '#475569'
                  }}>
                    {getCampaignStatusLabel(campaign.status)}
                  </span>
                  <button
                    type="button"
                    title={language === 'ar' ? 'نسخ الحملة' : 'Duplicate campaign'}
                    onClick={() => {
                      let tf = {};
                      try { tf = typeof campaign.target_filter === 'string' ? JSON.parse(campaign.target_filter || '{}') : (campaign.target_filter || {}); } catch (_) {}
                      setCampaignForm({
                        name: (language === 'ar' ? 'نسخة من ' : 'Copy of ') + campaign.name,
                        template_name: campaign.template_name || '',
                        scheduled_for: '',
                        target_filter: {
                          client_readiness: tf.client_readiness || null,
                          property_type: tf.property_type || null,
                          area: tf.area || null,
                          neighborhood: tf.neighborhood || null,
                          tags: tf.tags || [],
                          manual_numbers: [],
                          source_phone_number_id: tf.source_phone_number_id || null,
                          recipient_mode: tf.recipient_mode || 'auto',
                        },
                      });
                      setManualRecipientsInput('');
                      setUploadedRecipientFileName('');
                      setShowCreateForm(true);
                      setShowFilters(false);
                      setConfirmSendOpen(false);
                      setTestSendStatus(null);
                      setTestSendMessage('');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={{ padding: '3px 10px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '6px', cursor: 'pointer', color: '#64748b', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <FaCopy style={{ fontSize: '10px' }} />
                    {language === 'ar' ? 'نسخ' : 'Copy'}
                  </button>
                </div>
              </div>

              {(() => {
                const stats = getCampaignStats(campaign);
                const safeTarget = Math.max(1, Number(stats.target || 0));
                const deliveredPct = Math.min(100, Math.round((Number(stats.delivered || 0) / safeTarget) * 100));
                const readPct = Math.min(100, Math.round((Number(stats.read || 0) / safeTarget) * 100));
                const failedPct = Math.min(100, Math.round((Number(stats.failed || 0) / safeTarget) * 100));
                const hasSentAny = stats.sent > 0 || stats.delivered > 0 || stats.failed > 0;
                const canDrilldown = ['completed', 'sending', 'running'].includes(String(campaign.status || '').toLowerCase());
                const STAT_ITEMS = [
                  { labelAr: 'المستهدفون', labelEn: 'Target', filter: 'all', value: stats.target, color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
                  { labelAr: 'وصل', labelEn: 'Delivered', filter: 'delivered', value: stats.delivered, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                  { labelAr: 'قُرئ', labelEn: 'Read', filter: 'read', value: stats.read, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                  { labelAr: 'فشل', labelEn: 'Failed', filter: 'failed', value: stats.failed, color: stats.failed > 0 ? '#dc2626' : '#94a3b8', bg: stats.failed > 0 ? '#fef2f2' : '#f8fafc', border: stats.failed > 0 ? '#fecaca' : '#e2e8f0' },
                ];
                return (
                  <div style={{ marginTop: '10px' }}>
                    {/* 4-stat grid — click any block to see that recipient list */}
                    {canDrilldown && (
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textAlign: 'center' }}>
                        {language === 'ar' ? '← اضغط على أي رقم لعرض قائمة المستلمين' : 'Click any number to view that recipients list →'}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px', marginBottom: '8px' }}>
                      {STAT_ITEMS.map((s) => (
                        <div
                          key={s.labelEn}
                          onClick={() => canDrilldown ? openRecipientDrilldown(campaign.id, s.filter) : undefined}
                          style={{
                            background: s.bg, border: `1px solid ${s.border}`, borderRadius: '8px', padding: '7px 3px', textAlign: 'center',
                            cursor: canDrilldown ? 'pointer' : 'default',
                            transition: 'transform 0.1s, box-shadow 0.1s',
                          }}
                          title={canDrilldown ? (language === 'ar' ? `عرض قائمة ${s.labelAr}` : `View ${s.labelEn} recipients`) : undefined}
                          onMouseEnter={(e) => { if (canDrilldown) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; } }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                        >
                          <div style={{ fontSize: '18px', fontWeight: '800', color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                          <div style={{ fontSize: '10px', fontWeight: '600', color: s.color, opacity: 0.8, marginTop: '2px' }}>
                            {language === 'ar' ? s.labelAr : s.labelEn}
                          </div>
                          {canDrilldown && <div style={{ fontSize: '9px', color: s.color, opacity: 0.5, marginTop: '1px' }}>▼</div>}
                        </div>
                      ))}
                    </div>
                    {/* Helper text explaining cumulative logic */}
                    {hasSentAny && stats.delivered > 0 && (
                      <div style={{ fontSize: '9px', color: '#94a3b8', textAlign: 'center', marginTop: '-4px', marginBottom: '4px', fontStyle: 'italic' }}>
                        {language === 'ar' 
                          ? '(وصل يشمل قُرئ — القراءة تعني أن الرسالة وصلت)'
                          : '(Delivered includes Read — reading means message was delivered)'}
                      </div>
                    )}
                    {/* Delivery progress bar */}
                    {hasSentAny && (
                      <div style={{ marginBottom: '6px' }}>
                        <div style={{ height: '6px', borderRadius: '9999px', background: '#e2e8f0', overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${deliveredPct}%`, height: '100%', background: '#16a34a', transition: 'width 0.5s' }} />
                          <div style={{ width: `${failedPct}%`, height: '100%', background: '#ef4444', transition: 'width 0.5s' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontSize: '10px' }}>
                          <span style={{ color: '#16a34a', fontWeight: 600 }}>{deliveredPct}% {language === 'ar' ? 'وصل' : 'delivered'}</span>
                          <span style={{ color: '#2563eb', fontWeight: 600 }}>{readPct}% {language === 'ar' ? 'قُرئ' : 'read'}</span>
                          {stats.failed > 0 && <span style={{ color: '#ef4444', fontWeight: 600 }}>{failedPct}% {language === 'ar' ? 'فشل' : 'failed'}</span>}
                        </div>
                      </div>
                    )}
                    {/* Manager expanded details */}
                    {isManagerOrOwner && expandedManagerCampaignId === campaign.id && (
                      <div style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #fed7aa', background: '#fffaf5', fontSize: '11px', color: '#9a3412', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <span><strong>{language === 'ar' ? 'معلق' : 'Pending'}:</strong> {stats.pending}</span>
                        <span><strong>{language === 'ar' ? 'حالة الحملة' : 'Status'}:</strong> {getCampaignStatusLabel(campaign.status)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      ) : campaigns.length > 0 ? (
        <div
          className="wa-empty-state"
          style={{
            padding: '22px',
            border: '1px dashed #cbd5e1',
            background: '#f8fafc'
          }}
        >
          <div className="wa-empty-state-icon">
            <FaFilter />
          </div>
          <p style={{ margin: 0 }}>{language === 'ar' ? 'لا توجد حملات في هذا التصنيف' : 'No campaigns in this filter'}</p>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
            {language === 'ar' ? 'غيّر الفلتر أو اعرض كل الحملات.' : 'Change filter or show all campaigns.'}
          </p>
          <button
            type="button"
            className="wa-btn wa-btn-secondary"
            onClick={() => setStatusFilter('all')}
            style={{ marginTop: '12px', height: '34px', padding: '0 14px', fontSize: '12px' }}
          >
            {language === 'ar' ? 'عرض كل الحملات' : 'Show all campaigns'}
          </button>
        </div>
      ) : (
        <div style={{ border: '1px dashed #cbd5e1', background: '#f8fafc', borderRadius: '12px', padding: '18px', color: '#64748b', fontSize: '13px' }}>
          {language === 'ar'
            ? 'لا توجد حملات ضمن التصنيف المحدد حالياً.'
            : 'No campaigns in the selected status right now.'}
        </div>
      )}

      {detailCampaignId && (() => {
        const c = campaigns.find((x) => x.id === detailCampaignId);
        if (!c) return null;
        const isEditMode = editingCampaignId === c.id;
        const form = isEditMode ? editingCampaignForm : null;
        let tf = {};
        try { tf = (typeof c.target_filter === 'string' ? JSON.parse(c.target_filter || '{}') : c.target_filter) || {}; } catch (_) {}
        const canEdit = c.status === 'draft' || c.status === 'scheduled';
        // Render via portal into document.body so position:fixed is always relative to the viewport
        const portalTarget = document.body;
        const closeModal = () => {
          setDetailCampaignId(null);
          setEditingCampaignId(null);
          setEditingCampaignForm(null);
          setRecipientReportCampaignId(null);
          setRecipientReportRows([]);
          setRecipientReportSummary(null);
          setRecipientReportError('');
          setRecipientSearch('');
          setTargetedRecipients([]);
          setCampaignTraceTab('recipients');
          setDeliveryEvents([]);
          setDeliveryEventsError('');
          setAuditEntries([]);
          setAuditEntriesError('');
          setTimelineDrawer(null);
        };
        return ReactDOM.createPortal(
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '16px' }} onClick={closeModal}>
            <div style={{ background: '#fff', borderRadius: '16px', padding: '0', maxWidth: '720px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
              {/* Modal header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>{isEditMode && form ? form.name : c.name}</h3>
                  <div style={{ marginTop: '4px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{language === 'ar' ? 'القالب' : 'Template'}: <strong>{c.template_name}</strong></span>
                    <span style={{
                      padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '700',
                      background: c.status === 'completed' ? '#f0fdf4' : c.status === 'cancelled' ? '#f1f5f9' : ['sending','running'].includes(c.status) ? '#ffedd5' : c.status === 'scheduled' ? '#fef3c7' : '#f1f5f9',
                      color: c.status === 'completed' ? '#166534' : c.status === 'cancelled' ? '#94a3b8' : ['sending','running'].includes(c.status) ? '#c2410c' : c.status === 'scheduled' ? '#b45309' : '#475569'
                    }}>{getCampaignStatusLabel(c.status)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  {canEdit && !isEditMode && (
                    <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => {
                      let f = {};
                      try { f = (typeof c.target_filter === 'string' ? JSON.parse(c.target_filter || '{}') : c.target_filter) || {}; } catch (_) {}
                      setEditingCampaignId(c.id);
                      setEditingCampaignForm({ name: c.name, template_name: c.template_name, target_filter: { ...f } });
                    }}>
                      <FaEdit style={{ marginRight: '4px' }} />{language === 'ar' ? 'تعديل' : 'Edit'}
                    </button>
                  )}
                  <button type="button" onClick={closeModal} style={{ width: '32px', height: '32px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', cursor: 'pointer', fontSize: '16px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              </div>
              {/* Scrollable body */}
              <div ref={modalBodyRef} style={{ overflowY: 'auto', flex: 1, padding: '20px 24px 24px' }}>
              {isEditMode && form ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{language === 'ar' ? 'اسم الحملة' : 'Name'}</label>
                    <input type="text" className="wa-input" value={form.name} onChange={(e) => setEditingCampaignForm({ ...form, name: e.target.value })} style={{ width: '100%', marginTop: '4px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{language === 'ar' ? 'القالب' : 'Template'}</label>
                    <select className="wa-input" value={form.template_name} onChange={(e) => setEditingCampaignForm({ ...form, template_name: e.target.value })} style={{ width: '100%', marginTop: '4px' }}>
                      {(metaTemplates.length > 0 ? metaTemplates : [{ name: 'hello_world' }]).map((t) => (
                        <option key={t.name} value={t.name}>{language === 'ar' ? (t.label_ar || t.name) : (t.label_en || t.name)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>{language === 'ar' ? 'رقم الإرسال' : 'Sending number'}</label>
                    <select
                      className="wa-input"
                      value={form.target_filter?.source_phone_number_id || ''}
                      onChange={(e) => setEditingCampaignForm({
                        ...form,
                        target_filter: {
                          ...(form.target_filter || {}),
                          source_phone_number_id: e.target.value || null
                        }
                      })}
                      style={{ width: '100%', marginTop: '4px' }}
                    >
                      <option value="">{language === 'ar' ? 'الافتراضي (المتصل)' : 'Default connected line'}</option>
                      {connectedIntegrations.map((it) => (
                        <option key={it.phone_number_id} value={it.phone_number_id}>
                          {it.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px', marginTop: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>{language === 'ar' ? 'استهداف الجمهور' : 'Target audience'}</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', display: 'block' }}>{language === 'ar' ? 'جاهزية العميل' : 'Client Readiness'}</label>
                          <select className="wa-input" value={form.target_filter?.client_readiness || ''} onChange={(e) => setEditingCampaignForm({ ...form, target_filter: { ...form.target_filter, client_readiness: e.target.value || null } })} style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px' }}>
                            <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                            <option value="high">{language === 'ar' ? 'عالية' : 'High'}</option>
                            <option value="medium">{language === 'ar' ? 'متوسطة' : 'Medium'}</option>
                            <option value="low">{language === 'ar' ? 'منخفضة' : 'Low'}</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', display: 'block' }}>{language === 'ar' ? 'نوع العقار' : 'Property'}</label>
                          <select className="wa-input" value={form.target_filter?.property_type || ''} onChange={(e) => setEditingCampaignForm({ ...form, target_filter: { ...form.target_filter, property_type: e.target.value || null } })} style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px' }}>
                            <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                            <option value="apartment">{language === 'ar' ? 'شقة' : 'Apartment'}</option>
                            <option value="villa">{language === 'ar' ? 'فيلا' : 'Villa'}</option>
                            <option value="land">{language === 'ar' ? 'أرض' : 'Land'}</option>
                            <option value="commercial">{language === 'ar' ? 'تجاري' : 'Commercial'}</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>{language === 'ar' ? '📍 الموقع الجغرافي' : '📍 Location'}</div>
                        <div style={{ padding: '12px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '10px', borderLeft: '4px solid #f97316' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#0f172a', marginBottom: '6px', display: 'block' }}>{language === 'ar' ? 'المنطقة' : 'Area'}</label>
                          <select className="wa-input" value={form.target_filter?.area || ''} onChange={(e) => {
                            const area = e.target.value || null;
                            const hoods = area ? (NEIGHBORHOOD_BY_AREA[area] || []) : [];
                            const curNeighborhood = form.target_filter?.neighborhood;
                            const validNeighborhood = curNeighborhood && hoods.some((h) => h.value === curNeighborhood) ? curNeighborhood : null;
                            setEditingCampaignForm({ ...form, target_filter: { ...form.target_filter, area, neighborhood: validNeighborhood } });
                          }} style={{ width: '100%', padding: '10px 12px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                            <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                            {AREA_GROUPS.map((g) => (
                              <optgroup key={g.groupAr} label={language === 'ar' ? g.groupAr : g.groupEn}>
                                {g.options.filter(a => a.value).map((a) => (
                                  <option key={a.value} value={a.value}>{language === 'ar' ? a.labelAr : a.labelEn}</option>
                                ))}
                              </optgroup>
                            ))}
                            {form.target_filter?.area && !AREA_OPTIONS.find((a) => a.value === form.target_filter?.area) && (
                              <option value={form.target_filter.area}>{form.target_filter.area}</option>
                            )}
                          </select>
                        </div>
                        <div style={{ padding: '12px 14px', background: form.target_filter?.area ? '#fff' : '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', opacity: form.target_filter?.area ? 1 : 0.85 }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#0f172a', marginBottom: '6px', display: 'block' }}>{language === 'ar' ? 'الحي' : 'Neighborhood'}</label>
                          <select className="wa-input" value={form.target_filter?.neighborhood || ''} onChange={(e) => setEditingCampaignForm({ ...form, target_filter: { ...form.target_filter, neighborhood: e.target.value || null } })} style={{ width: '100%', padding: '10px 12px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: form.target_filter?.area ? 'pointer' : 'not-allowed' }} disabled={!form.target_filter?.area}>
                            <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                            {(NEIGHBORHOOD_BY_AREA[form.target_filter?.area] || []).map((n) => (
                              <option key={n.value} value={n.value}>{language === 'ar' ? n.labelAr : n.labelEn}</option>
                            ))}
                          </select>
                          {!form.target_filter?.area && <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>{language === 'ar' ? 'اختر المنطقة أولاً' : 'Select area first'}</p>}
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', display: 'block' }}>{language === 'ar' ? 'التصنيفات' : 'Tags'}</label>
                        <input type="text" className="wa-input" value={Array.isArray(form.target_filter?.tags) ? form.target_filter.tags.join(', ') : (form.target_filter?.tags || '')} onChange={(e) => { const v = e.target.value; const arr = v ? v.split(/[,،]/).map(s => s.trim()).filter(Boolean) : []; setEditingCampaignForm({ ...form, target_filter: { ...form.target_filter, tags: arr } }); }} placeholder={language === 'ar' ? 'vip، مستثمر' : 'vip, investor'} style={{ width: '100%', padding: '8px 10px', fontSize: '12px', borderRadius: '6px' }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button className="wa-btn wa-btn-primary" style={{ padding: '8px 16px' }} aria-label={language === 'ar' ? 'حفظ التعديلات' : 'Save changes'} onClick={async () => {
                      try {
                        await updateBroadcastCampaign(c.id, { name: form.name, template_name: form.template_name, target_filter: form.target_filter });
                        setEditingCampaignId(null); setEditingCampaignForm(null); setDetailCampaignId(null);
                        loadCampaigns();
                      } catch (e) { toast.error(e?.response?.data?.error || (language === 'ar' ? 'حدث خطأ' : 'An error occurred')); }
                    }}>{language === 'ar' ? 'حفظ' : 'Save'}</button>
                    <button className="wa-btn wa-btn-secondary" style={{ padding: '8px 16px' }} onClick={() => { setEditingCampaignId(null); setEditingCampaignForm(null); }} aria-label={language === 'ar' ? 'إلغاء التعديل' : 'Cancel edit'}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Big stat blocks */}
                  {(() => {
                    const s = getCampaignStats(c);
                    const effectiveTarget = Math.max(s.target, targetedRecipients.length);
                    s.target = effectiveTarget;
                    const safeT = Math.max(1, s.target);
                    const dPct = Math.min(100, Math.round((s.delivered / safeT) * 100));
                    const rPct = Math.min(100, Math.round((s.read / safeT) * 100));
                    const fPct = Math.min(100, Math.round((s.failed / safeT) * 100));
                    const MODAL_STATS = [
                      { labelAr: 'المستهدفون', labelEn: 'Target', value: s.target, color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
                      { labelAr: 'أُرسل', labelEn: 'Sent', value: s.sent, color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff' },
                      { labelAr: 'وصل', labelEn: 'Delivered', value: s.delivered, pct: dPct, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                      { labelAr: 'قُرئ', labelEn: 'Read', value: s.read, pct: rPct, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                      { labelAr: 'فشل', labelEn: 'Failed', value: s.failed, pct: fPct, color: s.failed > 0 ? '#dc2626' : '#94a3b8', bg: s.failed > 0 ? '#fef2f2' : '#f8fafc', border: s.failed > 0 ? '#fecaca' : '#e2e8f0' },
                    ];
                    return (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '12px' }}>
                          {MODAL_STATS.map((st) => (
                            <div key={st.labelEn} style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: '10px', padding: '12px 6px', textAlign: 'center' }}>
                              <div style={{ fontSize: '28px', fontWeight: '900', color: st.color, lineHeight: 1 }}>{st.value}</div>
                              {st.pct !== undefined && <div style={{ fontSize: '11px', color: st.color, fontWeight: '700', marginTop: '2px' }}>{st.pct}%</div>}
                              <div style={{ fontSize: '11px', color: st.color, opacity: 0.75, marginTop: '3px' }}>
                                {language === 'ar' ? st.labelAr : st.labelEn}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Progress bar */}
                        {s.target > 0 && (s.sent > 0 || s.delivered > 0 || s.failed > 0) && (
                          <div style={{ marginBottom: '14px' }}>
                            <div style={{ height: '8px', borderRadius: '9999px', background: '#e2e8f0', overflow: 'hidden', display: 'flex' }}>
                              <div style={{ width: `${dPct}%`, height: '100%', background: '#16a34a', transition: 'width 0.6s' }} title={`${language === 'ar' ? 'وصل' : 'Delivered'}: ${dPct}%`} />
                              <div style={{ width: `${fPct}%`, height: '100%', background: '#ef4444', transition: 'width 0.6s' }} title={`${language === 'ar' ? 'فشل' : 'Failed'}: ${fPct}%`} />
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '5px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                              <span>🟢 {language === 'ar' ? 'وصل' : 'Delivered'}: {dPct}%</span>
                              <span>🔵 {language === 'ar' ? 'قُرئ' : 'Read'}: {rPct}%</span>
                              {s.failed > 0 && <span>🔴 {language === 'ar' ? 'فشل' : 'Failed'}: {fPct}%</span>}
                              <span style={{ color: '#94a3b8' }}>{language === 'ar' ? 'أُرسل = قبلته ميتا | وصل = وصل للهاتف | قُرئ = فُتح' : 'Sent = accepted by Meta · Delivered = reached device · Read = opened'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Campaign meta info */}
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                    <span><strong>{language === 'ar' ? 'رقم الإرسال' : 'Sender'}:</strong> {getIntegrationLabelById(tf?.source_phone_number_id)}</span>
                    {c.scheduled_for && <span><strong>{language === 'ar' ? 'مجدولة' : 'Scheduled'}:</strong> {new Date(c.scheduled_for).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}</span>}
                    {c.started_at && <span><strong>{language === 'ar' ? 'بدأت' : 'Started'}:</strong> {new Date(c.started_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}</span>}
                    {c.completed_at && <span><strong>{language === 'ar' ? 'انتهت' : 'Completed'}:</strong> {new Date(c.completed_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}</span>}
                    <span><strong>{language === 'ar' ? 'الفلاتر' : 'Filters'}:</strong> {Object.entries(tf).filter(([, v]) => v && !(Array.isArray(v) && !v.length)).map(([k]) => k).join(', ') || (language === 'ar' ? 'الكل' : 'All')}</span>
                  </div>

                  {/* Targeted recipients section */}
                  {targetedRecipients.length > 0 && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#faf5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#7c3aed' }}>
                          🎯 {language === 'ar' ? `المستهدفون (${targetedRecipients.length})` : `Targeted (${targetedRecipients.length})`}
                        </div>
                      </div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {targetedRecipients.map((t, idx) => (
                          <div key={t.phone || idx} style={{ padding: '8px 14px', borderBottom: '1px solid #f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {t.display_name && <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{t.display_name}</span>}
                              <span style={{ fontSize: t.display_name ? '11px' : '13px', fontWeight: t.display_name ? '500' : '700', color: t.display_name ? '#64748b' : '#0f172a', fontFamily: 'monospace' }}>{t.phone}</span>
                            </div>
                            <span style={{
                              fontSize: '10px', fontWeight: 700, borderRadius: '9999px', padding: '2px 8px',
                              background: t.in_recipients ? '#f0fdf4' : '#fef2f2',
                              color: t.in_recipients ? '#16a34a' : '#dc2626',
                              border: `1px solid ${t.in_recipients ? '#bbf7d0' : '#fecaca'}`
                            }}>
                              {t.in_recipients
                                ? (language === 'ar' ? '✅ تم الإرسال' : '✅ Sent')
                                : (language === 'ar' ? '⚠️ لم يُسجَّل' : '⚠️ Not tracked')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recipients report + trace tabs */}
                  <div ref={recipientSectionRef} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    {/* Report header */}
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                          {language === 'ar' ? '📋 المستلمون والتتبع' : '📋 Recipients & trace'}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {[
                            { id: 'recipients', labelAr: 'المستلمون', labelEn: 'Recipients' },
                            { id: 'delivery', labelAr: 'سجل التسليم', labelEn: 'Delivery log' },
                            { id: 'activity', labelAr: 'النشاط', labelEn: 'Activity' }
                          ].map((tab) => {
                            const active = campaignTraceTab === tab.id;
                            return (
                              <button key={tab.id} type="button" onClick={() => setCampaignTraceTab(tab.id)} style={{
                                border: `1px solid ${active ? '#0f766e' : '#e2e8f0'}`,
                                background: active ? '#ecfdf5' : '#fff',
                                color: active ? '#0f766e' : '#64748b',
                                borderRadius: '8px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer'
                              }}>
                                {language === 'ar' ? tab.labelAr : tab.labelEn}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '5px 10px', fontSize: '11px' }} onClick={() => { setRecipientSearch(''); loadRecipientReport(c.id); }}>
                          <FaSyncAlt style={{ fontSize: '10px', marginRight: '4px' }} />{language === 'ar' ? 'تحديث' : 'Refresh'}
                        </button>
                      </div>
                    </div>

                    {recipientReportLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                        <div style={{ marginBottom: '8px', fontSize: '20px' }}>⏳</div>
                        {language === 'ar' ? 'جاري تحميل قائمة المستلمين...' : 'Loading recipients…'}
                      </div>
                    ) : recipientReportError ? (
                      <div style={{ padding: '16px', color: '#b91c1c', fontSize: '13px' }}>{recipientReportError}</div>
                    ) : recipientReportCampaignId === c.id ? (
                      <div>
                        {campaignTraceTab === 'recipients' && (
                        <>
                        {/* Status filter tabs */}
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {[
                            { id: 'all', key: 'total', labelAr: 'الكل', labelEn: 'All', color: '#475569' },
                            { id: 'delivered', key: 'delivered', labelAr: 'وصل', labelEn: 'Delivered', color: '#16a34a' },
                            { id: 'read', key: 'read', labelAr: 'قُرئ', labelEn: 'Read', color: '#2563eb' },
                            { id: 'sent', key: 'sent', labelAr: 'أُرسل', labelEn: 'Sent', color: '#7c3aed' },
                            { id: 'failed', key: 'failed', labelAr: 'فشل', labelEn: 'Failed', color: '#dc2626' },
                            { id: 'replied', key: 'replied', labelAr: 'ردّ', labelEn: 'Replied', color: '#0f766e' },
                          ].map((item) => {
                            const count = Number(recipientReportSummary?.[item.key] || 0);
                            const active = recipientStatusFilter === item.id;
                            return (
                              <button key={item.id} type="button" onClick={() => setRecipientStatusFilter(item.id)} style={{
                                border: `1px solid ${active ? item.color : '#e2e8f0'}`,
                                background: active ? item.color : '#fff',
                                color: active ? '#fff' : '#475569',
                                borderRadius: '9999px', padding: '4px 10px',
                                fontSize: '11px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s'
                              }}>
                                {language === 'ar' ? item.labelAr : item.labelEn} <span style={{ opacity: 0.85 }}>({count})</span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Search box */}
                        <div style={{ padding: '8px 14px', borderBottom: '1px solid #f1f5f9' }}>
                          <input
                            type="text"
                            value={recipientSearch}
                            onChange={(e) => setRecipientSearch(e.target.value)}
                            placeholder={language === 'ar' ? '🔍 ابحث برقم الهاتف...' : '🔍 Search by phone number…'}
                            style={{ width: '100%', padding: '7px 12px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', background: '#f8fafc', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* Failure category summary — shown when viewing failed tab */}
                        {recipientStatusFilter === 'failed' && (() => {
                          const failedRows = (recipientReportRows || []).filter((r) => String(r.status || '').toLowerCase() === 'failed');
                          if (failedRows.length === 0) return null;
                          // Group rows by category
                          const catRows = {};
                          failedRows.forEach((r) => {
                            const cat = getErrorCategory(r.error_message || r.errorMessage || '');
                            if (!catRows[cat]) catRows[cat] = [];
                            catRows[cat].push(r);
                          });
                          const isRTL = language === 'ar';
                          const allRetryableIds = failedRows.filter((r) => isRetryableError(r.error_message || r.errorMessage || '')).map((r) => r.id);
                          const isBulkRetrying = retryingRecipients.size > 0;
                          return (
                            <div style={{ margin: '10px 14px', display: 'grid', gap: '8px' }}>
                              {/* Bulk retry bar */}
                              {allRetryableIds.length > 0 && (
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', direction: isRTL ? 'rtl' : 'ltr' }}>
                                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                                    {isRTL
                                      ? `${allRetryableIds.length} مستلم قابل لإعادة الإرسال`
                                      : `${allRetryableIds.length} recipient(s) retryable`}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={isBulkRetrying}
                                    onClick={() => confirmRetry(allRetryableIds)}
                                    style={{ padding: '5px 14px', fontSize: '11px', fontWeight: 700, border: '1px solid #f97316', borderRadius: '7px', background: isBulkRetrying ? '#fed7aa' : '#fff7ed', color: '#c2410c', cursor: isBulkRetrying ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {isBulkRetrying
                                      ? (isRTL ? '⏳ جاري...' : '⏳ Retrying...')
                                      : (isRTL ? `🔄 إعادة إرسال الكل (${allRetryableIds.length})` : `🔄 Retry all (${allRetryableIds.length})`)}
                                  </button>
                                </div>
                              )}
                              {/* Per-category cards */}
                              {Object.entries(catRows).map(([cat, rows]) => {
                                const meta = FAILURE_CATEGORY_META[cat] || FAILURE_CATEGORY_META.unknown;
                                const errEntry = Object.values(META_ERROR_MAP).find((e) => e.category === cat);
                                const advice = errEntry ? (isRTL ? errEntry.adviceAr : errEntry.adviceEn) : null;
                                const retryableIds = rows.filter((r) => isRetryableError(r.error_message || r.errorMessage || '')).map((r) => r.id);
                                const isCatRetrying = retryableIds.some((id) => retryingRecipients.has(id));
                                return (
                                  <div key={cat} style={{ background: meta.colorBg, border: `1px solid ${meta.colorBorder}`, borderRadius: '10px', padding: '10px 14px', direction: isRTL ? 'rtl' : 'ltr' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '16px' }}>{meta.icon}</span>
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: meta.colorText }}>
                                          {isRTL ? meta.labelAr : meta.labelEn}
                                        </span>
                                        <span style={{ fontSize: '12px', fontWeight: 800, color: meta.colorText, background: '#fff', border: `1px solid ${meta.colorBorder}`, borderRadius: '9999px', padding: '1px 10px' }}>
                                          {rows.length}
                                        </span>
                                      </div>
                                      {retryableIds.length > 0 && (
                                        <button
                                          type="button"
                                          disabled={isCatRetrying}
                                          onClick={() => confirmRetry(retryableIds)}
                                          style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 700, border: `1px solid ${meta.colorBorder}`, borderRadius: '6px', background: isCatRetrying ? meta.colorBg : '#fff', color: meta.colorText, cursor: isCatRetrying ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                                        >
                                          {isCatRetrying
                                            ? (isRTL ? '⏳ جاري...' : '⏳ Retrying...')
                                            : (isRTL ? `🔄 إعادة إرسال (${retryableIds.length})` : `🔄 Retry (${retryableIds.length})`)}
                                        </button>
                                      )}
                                      {retryableIds.length === 0 && (
                                        <span style={{ fontSize: '10px', color: meta.colorText, opacity: 0.6, fontStyle: 'italic' }}>
                                          {isRTL ? 'لا تنصح بإعادة الإرسال' : 'No retry recommended'}
                                        </span>
                                      )}
                                    </div>
                                    {advice && (
                                      <div style={{ marginTop: '6px', fontSize: '11px', color: meta.colorText, opacity: 0.85, lineHeight: '1.6', paddingInlineStart: '24px' }}>
                                        💡 {advice}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Recipient rows */}
                        {(() => {
                          const STATUS_ICON = { delivered: '✅', read: '👁️', sent: '📤', failed: '❌', pending: '⏳', replied: '💬' };
                          const STATUS_COLOR = { delivered: '#16a34a', read: '#2563eb', sent: '#7c3aed', failed: '#dc2626', pending: '#b45309', replied: '#0f766e' };
                          const filtered = (recipientReportRows || []).filter((row) => {
                            const statusMatch = recipientStatusFilter === 'all' ? true
                              : recipientStatusFilter === 'replied' ? Boolean(row.has_reply)
                              : String(row.status || '').toLowerCase() === recipientStatusFilter;
                            const searchMatch = !recipientSearch.trim() || String(row.phone || row.conversation_id || '').includes(recipientSearch.trim());
                            return statusMatch && searchMatch;
                          });
                          const total = (recipientReportRows || []).length;
                          return (
                            <div>
                              {total > 0 && (
                                <div style={{ padding: '6px 14px', fontSize: '11px', color: '#94a3b8', borderBottom: '1px solid #f8fafc' }}>
                                  {language === 'ar' ? `عرض ${filtered.length} من ${total}` : `Showing ${filtered.length} of ${total}`}
                                </div>
                              )}
                              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                {filtered.length === 0 ? (
                                  <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                                    {language === 'ar' ? 'لا توجد نتائج.' : 'No results.'}
                                  </div>
                                ) : filtered.map((row) => {
                                  const st = String(row.status || 'pending').toLowerCase();
                                  const icon = STATUS_ICON[st] || '•';
                                  const col = STATUS_COLOR[st] || '#64748b';
                                  return (
                                    <div key={row.id} style={{ padding: '10px 14px', borderBottom: '1px solid #f8fafc', display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'start' }}>
                                      <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                          {row.display_name && <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{row.display_name}</span>}
                                          <span style={{ fontSize: row.display_name ? '11px' : '13px', fontWeight: row.display_name ? '500' : '700', color: row.display_name ? '#64748b' : '#0f172a', fontFamily: 'monospace' }}>{row.phone || row.conversation_id}</span>
                                          {row.has_reply && <span style={{ fontSize: '10px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '9999px', padding: '1px 6px', fontWeight: 700 }}>💬 {language === 'ar' ? 'ردّ' : 'Replied'}</span>}
                                        </div>
                                        {row.sent_at && (
                                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                            {language === 'ar' ? 'إرسال' : 'Sent'}: {new Date(row.sent_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}
                </div>
              )}
                                        {row.last_correlation_id && (
                                          <div
                                            style={{ fontSize: '9px', color: '#94a3b8', fontFamily: 'monospace', marginTop: '2px' }}
                                            title={row.last_correlation_id}
                                          >
                                            {language === 'ar' ? 'آخر محاولة' : 'Last attempt'}: …{String(row.last_correlation_id).slice(-10)}
                                          </div>
                                        )}
                                        {st === 'failed' && (() => {
                                          const rawErr = String(row.error_message || row.errorMessage || '').trim();
                                          const mapped = translateMetaError(rawErr);
                                          const cat = getErrorCategory(rawErr);
                                          const catMeta = FAILURE_CATEGORY_META[cat] || FAILURE_CATEGORY_META.unknown;
                                          const codeMatch = rawErr.match(/\[(\d+)\]/);
                                          const errorCode = codeMatch ? codeMatch[1] : null;
                                          const displayErr = mapped ? (language === 'ar' ? mapped.ar : mapped.en) : null;
                                          const hasArabicInRaw = /[\u0600-\u06FF]/.test(rawErr);
                                          const fallbackErr = hasArabicInRaw ? rawErr.replace(/^\[\d+\]\s*/, '') : (language === 'ar' ? 'لا يوجد تفاصيل متاحة' : (rawErr || 'No details available'));
                                          const fullMsg = displayErr || fallbackErr;
                                          const shortErr = fullMsg.length > 55 ? fullMsg.slice(0, 52) + '...' : fullMsg;
                                          const canRetry = isRetryableError(rawErr);
                                          const isRetrying = retryingRecipients.has(row.id);
                                          const isRTL = language === 'ar';
                                          return (
                                            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr' }}>
                                              <span style={{ fontSize: '12px' }}>{catMeta.icon}</span>
                                              <span
                                                title={fullMsg}
                                                style={{ fontSize: '11px', color: catMeta.colorText, background: catMeta.colorBg, border: `1px solid ${catMeta.colorBorder}`, borderRadius: '6px', padding: '2px 8px', fontWeight: 600, cursor: 'help', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}
                                              >
                                                {shortErr}
                                              </span>
                                              {errorCode && <span style={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>#{errorCode}</span>}
                                              {canRetry && (
                                                <button
                                                  type="button"
                                                  disabled={isRetrying}
                                                  onClick={(e) => { e.stopPropagation(); confirmRetry([row.id]); }}
                                                  style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 700, border: `1px solid ${catMeta.colorBorder}`, borderRadius: '5px', background: isRetrying ? catMeta.colorBg : '#fff', color: catMeta.colorText, cursor: isRetrying ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                                                >
                                                  {isRetrying ? '⏳' : '🔄'}
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {row.last_reply_at && (
                                          <div style={{ marginTop: '3px', fontSize: '11px', color: '#0f766e' }}>
                                            💬 {language === 'ar' ? 'آخر رد' : 'Replied'}: {new Date(row.last_reply_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en')}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                        <button
                                          type="button"
                                          className="wa-btn wa-btn-secondary"
                                          title={language === 'ar' ? 'عرض سجل التسليم لهذا المستلم' : 'View delivery timeline for this recipient'}
                                          style={{ padding: '4px 8px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTimelineDrawer({
                                              campaignId: c.id,
                                              recipientId: row.id,
                                              phone: row.phone,
                                              displayName: row.display_name || row.displayName
                                            });
                                          }}
                                        >
                                          <FaHistory style={{ fontSize: '11px' }} />
                                          {language === 'ar' ? 'سجل' : 'Trace'}
                                        </button>
                                      <span style={{ fontSize: '12px', fontWeight: '700', color: col, background: col + '15', border: `1px solid ${col}30`, borderRadius: '9999px', padding: '3px 8px', whiteSpace: 'nowrap' }}>
                                        {icon} {language === 'ar' ? ({ delivered: 'وصل', read: 'قُرئ', sent: 'أُرسل', failed: 'فشل', pending: 'معلق' }[st] || st) : st}
                                      </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                        </>
                        )}

                        {campaignTraceTab === 'delivery' && (
                          <div style={{ padding: '12px 14px' }}>
                            {deliveryEventsLoading ? (
                              <div style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', padding: '24px' }}>{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</div>
                            ) : deliveryEventsError ? (
                              <div style={{ color: '#b91c1c', fontSize: '13px' }}>{deliveryEventsError}</div>
                            ) : deliveryEvents.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '20px', lineHeight: 1.6 }}>
                                {language === 'ar'
                                  ? 'لا توجد أحداث مسجّلة بعد. تظهر الأحداث بعد الإرسال مع إصدار الخادم الحالي.'
                                  : 'No delivery events yet. Events are recorded after sends on the current server version.'}
                              </div>
                            ) : (
                              <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                {deliveryEvents.map((ev) => (
                                  <div key={ev.id} style={{ borderBottom: '1px solid #f1f5f9', padding: '10px 0' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontSize: '11px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                                      <span style={{ color: '#94a3b8' }}>{ev.created_at ? new Date(ev.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en') : '—'}</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{ev.status}</span>
                                      <span style={{ color: '#475569' }}>{deliverySourceLabel(ev.source)}</span>
                                      {ev.error_code != null && ev.error_code !== '' && (
                                        <span style={{ fontFamily: 'monospace', color: '#b91c1c', fontWeight: 600 }}>#{ev.error_code}</span>
                                      )}
                                      <span style={{ color: '#94a3b8', fontSize: '10px' }}>{language === 'ar' ? 'مستلم' : 'rcp'} #{ev.recipient_id}</span>
                                      <div style={{ display: 'flex', gap: '4px', marginInlineStart: 'auto' }}>
                                        {ev.correlation_id && (
                                          <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '2px 8px', fontSize: '9px' }} onClick={() => copyTraceId(ev.correlation_id)} title={ev.correlation_id}>correlation</button>
                                        )}
                                        {ev.send_batch_id && (
                                          <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '2px 8px', fontSize: '9px' }} onClick={() => copyTraceId(ev.send_batch_id)} title={ev.send_batch_id}>batch</button>
                                        )}
                                      </div>
                                    </div>
                                    {(ev.error_detail || ev.error_title) && (
                                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px', lineHeight: 1.5, direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                                        {ev.error_title ? `${ev.error_title}: ` : ''}{ev.error_detail || ''}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {campaignTraceTab === 'activity' && (
                          <div style={{ padding: '12px 14px' }}>
                            {auditEntriesLoading ? (
                              <div style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', padding: '24px' }}>{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</div>
                            ) : auditEntriesError ? (
                              <div style={{ color: '#b91c1c', fontSize: '13px' }}>{auditEntriesError}</div>
                            ) : auditEntries.length === 0 ? (
                              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '20px' }}>
                                {language === 'ar' ? 'لا توجد إدخالات نشاط بعد.' : 'No activity entries yet.'}
                              </div>
                            ) : (
                              <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {auditEntries.map((en) => {
                                  let metaObj = en.metadata;
                                  if (typeof metaObj === 'string') {
                                    try { metaObj = JSON.parse(metaObj); } catch (_) { metaObj = {}; }
                                  }
                                  const hasMeta = metaObj && typeof metaObj === 'object' && Object.keys(metaObj).length > 0;
                                  return (
                                  <div key={en.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', background: '#fafafa', fontSize: '11px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{en.action}</span>
                                      <span style={{ color: '#94a3b8' }}>{en.created_at ? new Date(en.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en') : ''}</span>
                                    </div>
                                    <div style={{ marginTop: '4px', color: '#64748b' }}>
                                      {language === 'ar' ? 'المستخدم' : 'User'} #{en.actor_user_id}
                                      {en.ip ? ` · IP ${en.ip}` : ''}
                                    </div>
                                    {hasMeta && (
                                      <pre style={{ margin: '8px 0 0', fontSize: '10px', background: '#fff', padding: '8px', borderRadius: '6px', overflow: 'auto', maxHeight: '120px', border: '1px solid #e2e8f0', textAlign: 'start' }}>{JSON.stringify(metaObj, null, 2)}</pre>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        {language === 'ar' ? 'لا تتوفر بيانات مستلمين لهذه الحملة.' : 'No recipient data available for this campaign.'}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>{/* end scrollable body */}
            </div>
          </div>
        , portalTarget);
      })()}

      {timelineDrawer && ReactDOM.createPortal(
        <>
          <div
            role="presentation"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100005 }}
            onClick={() => setTimelineDrawer(null)}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              ...(language === 'ar' ? { left: 0 } : { right: 0 }),
              width: 'min(420px, calc(100vw - 20px))',
              background: '#fff',
              zIndex: 100006,
              boxShadow: language === 'ar' ? '8px 0 28px rgba(0,0,0,0.15)' : '-8px 0 28px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              direction: language === 'ar' ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{language === 'ar' ? 'سجل التسليم' : 'Delivery timeline'}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', fontFamily: 'monospace' }}>
                  {timelineDrawer.displayName ? `${timelineDrawer.displayName} · ` : ''}{timelineDrawer.phone || `#${timelineDrawer.recipientId}`}
                </div>
              </div>
              <button type="button" aria-label="close" onClick={() => setTimelineDrawer(null)} style={{ border: 'none', background: '#f1f5f9', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer' }}>
                <FaTimes />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {timelineState.loading ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '24px', fontSize: '13px' }}>{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</div>
              ) : timelineState.error ? (
                <div style={{ color: '#b91c1c', fontSize: '13px' }}>{timelineState.error}</div>
              ) : timelineState.events.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '20px' }}>
                  {language === 'ar' ? 'لا توجد أحداث لهذا المستلم.' : 'No events for this recipient.'}
                </div>
              ) : (
                timelineState.events.map((ev) => (
                  <div key={ev.id} style={{ borderBottom: '1px solid #f1f5f9', padding: '12px 0' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{ev.created_at ? new Date(ev.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en') : ''}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, marginTop: '4px' }}>{ev.status} · {deliverySourceLabel(ev.source)}</div>
                    {ev.error_code != null && ev.error_code !== '' && (
                      <div style={{ fontSize: '11px', color: '#b91c1c', fontFamily: 'monospace', marginTop: '4px' }}>#{ev.error_code}</div>
                    )}
                    {(ev.error_detail || ev.error_title) && (
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', lineHeight: 1.5 }}>{ev.error_title ? `${ev.error_title}: ` : ''}{ev.error_detail || ''}</div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                      {ev.correlation_id && (
                        <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }} onClick={() => copyTraceId(ev.correlation_id)}>correlation</button>
                      )}
                      {ev.send_batch_id && (
                        <button type="button" className="wa-btn wa-btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }} onClick={() => copyTraceId(ev.send_batch_id)}>batch</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {retryConfirmation && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100001, padding: '16px' }} onClick={() => setRetryConfirmation(null)}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', direction: language === 'ar' ? 'rtl' : 'ltr', textAlign: language === 'ar' ? 'right' : 'left' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '20px', marginBottom: '12px', textAlign: 'center' }}>🔄</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '8px', textAlign: 'center' }}>
              {language === 'ar' ? 'تأكيد إعادة الإرسال' : 'Confirm Retry'}
            </div>
            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.7', marginBottom: '16px' }}>
              {retryConfirmation.recipientIds.length === 1
                ? (language === 'ar'
                  ? 'سيتم إعادة إرسال نفس قالب الحملة لهذا المستلم. هل تريد المتابعة؟'
                  : 'The same campaign template will be re-sent to this recipient. Continue?')
                : (language === 'ar'
                  ? `سيتم إعادة إرسال نفس قالب الحملة لـ ${retryConfirmation.recipientIds.length} مستلم. هل تريد المتابعة؟`
                  : `The same campaign template will be re-sent to ${retryConfirmation.recipientIds.length} recipients. Continue?`)}
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '11px', color: '#92400e', lineHeight: '1.6' }}>
              <span style={{ fontWeight: 700 }}>{language === 'ar' ? '⚠️ ملاحظة:' : '⚠️ Note:'}</span>{' '}
              {language === 'ar'
                ? 'بعض الرسائل الفاشلة بسبب حد واتساب التسويقي (#131049) قد تحتاج انتظار ٢٤ ساعة قبل إعادة الإرسال. إعادة الإرسال الفوري قد تفشل مرة أخرى.'
                : 'Messages that failed due to WhatsApp marketing limit (#131049) may need 24h before retry. Immediate retry may fail again.'}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button type="button" onClick={() => setRetryConfirmation(null)} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', color: '#475569', cursor: 'pointer' }}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button type="button" onClick={executeRetry} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 700, border: '1px solid #f97316', borderRadius: '8px', background: '#f97316', color: '#fff', cursor: 'pointer' }}>
                {retryConfirmation.recipientIds.length === 1
                  ? (language === 'ar' ? '🔄 إعادة الإرسال' : '🔄 Retry')
                  : (language === 'ar' ? `🔄 إعادة إرسال (${retryConfirmation.recipientIds.length})` : `🔄 Retry (${retryConfirmation.recipientIds.length})`)}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

        </>
      ) : null}
    </div>
  );
};

export default WhatsAppCampaigns;
